import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../components/shared/AuthContext';
// ledger_transaction.amount stores PAISE.
import { formatINR } from '../utils/money';
import {
  PageLayout,
  Table,
  Button,
  Badge,
  SegmentedControl,
  Modal,
  ConfirmModal,
  Spinner,
  useSnackbar,
} from '../components/ui';

/**
 * Day End Process — redesigned layout.
 *
 * A parallel page to Dayendprocess.js, which is left untouched. All logic here
 * (state, fetching, validateForm, handleSubmit, handleApprove) is a verbatim
 * copy; only the render differs. Keep the two in step until the old page is
 * retired — a fix applied to one must be applied to the other.
 *
 * What changed in the render:
 *   - the counted figure and the submit button share the top band, instead of
 *     sitting two scrolls apart
 *   - Cash and Online are equal-weight panels rather than cards of different
 *     heights, which is what made Loan Records float up beside Cash Records
 *   - the four always-open tables became tabs carrying row counts
 */
const DayEndProcessV2 = () => {
  // formError renders only inside the "Close the day" tab (:2557), but approval
  // happens in the Staff tab — so a failure there was written to state nobody
  // could see. The snackbar portals above the page and shows wherever the
  // operator is standing.
  const { showError, SnackbarHost } = useSnackbar();

  const [activeTab, setActiveTab] = useState('transactions');
  // Declared with the other state rather than mid-file as in the original — the
  // submit handler sets it, so it must exist before that handler is defined.
  const [showDayEnd, setShowDayEnd] = useState(true);
  const [cashRecords, setCashRecords] = useState([]);
  const [loanRecords, setLoanRecords] = useState([]);
  const [staffRecords, setStaffRecords] = useState([]);
  const [ledgerRecords, setLedgerRecords] = useState([]);
  const [ledgerAccounts, setLedgerAccounts] = useState([]);

  // The sticky tab strip, measured so the sticky table headings inside each tab
  // can park directly beneath it instead of overlapping it.
  //
  // Measured, never hard-coded: the strip wraps to two lines on a narrow window,
  // and a fixed pixel offset would then leave a gap or still overlap.
  const tabStripRef = useRef(null);
  const tabCardRef = useRef(null);

  // Keep --table-head-offset equal to the tab strip's height, so a sticky table
  // heading stops just below it rather than under it.
  //
  // Re-measured on resize AND via ResizeObserver, because the strip's height
  // changes when it wraps — a value read once at mount is wrong the moment the
  // window narrows.
  useEffect(() => {
    const strip = tabStripRef.current;
    const card = tabCardRef.current;
    if (!strip || !card) return undefined;

    const sync = () => {
      const h = strip.getBoundingClientRect().height;
      card.style.setProperty('--table-head-offset', `${Math.round(h)}px`);
    };

    sync();
    window.addEventListener('resize', sync);
    // Not available in older browsers; the resize listener alone still covers
    // the common case, so this is an enhancement rather than a requirement.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    if (ro) ro.observe(strip);

    return () => {
      window.removeEventListener('resize', sync);
      if (ro) ro.disconnect();
    };
  }, []);
  const [loadingCash, setLoadingCash] = useState(true);
  const [loadingLoan, setLoadingLoan] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingLedger, setLoadingLedger] = useState(true);
  const [errorCash, setErrorCash] = useState(null);
  const [errorLoan, setErrorLoan] = useState(null);
  const [errorStaff, setErrorStaff] = useState(null);
  const [errorLedger, setErrorLedger] = useState(null);
  const [filterType, setFilterType] = useState('All');
  const [transactionFilter, setTransactionFilter] = useState('All');
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState('All');
  const [ledgerAccountFilter, setLedgerAccountFilter] = useState('All');
  const [totalCashCollection, setTotalCashCollection] = useState('');
  const [cashDenominations, setCashDenominations] = useState({
    500: 0,
    200: 0,
    100: 0,
    50: 0,
    20: 0,
    10: 0,
  });
  const [change, setChange] = useState('');
  const [outstanding, setOutstanding] = useState('');
  const [expenses, setExpenses] = useState('');
  const [branchPayout, setBranchPayout] = useState('');
  const [branches, setBranches] = useState('');
  const [allBranches, setAllBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [formError, setFormError] = useState(null);
  const [formValid, setFormValid] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [dayEndStatus, setDayEndStatus] = useState('pending');
  // Every date still open for this branch, oldest first, from the server. The
  // date strip used to be a fixed guess at today/yesterday/two-days-before, which
  // could not reach an older backlog and silently dropped Sundays.
  const [pendingDates, setPendingDates] = useState([]);
  const [loadingDayEndStatus, setLoadingDayEndStatus] = useState(true);
  const [todaysDayEndRecord, setTodaysDayEndRecord] = useState(null);
  const [yesterdayDayEndStatus, setYesterdayDayEndStatus] = useState('pending');
  const [twoDaysBeforeDayEndStatus, setTwoDaysBeforeDayEndStatus] = useState('pending');
  const [yesterdayOutstanding, setYesterdayOutstanding] = useState(0);
  const [transactionDate, setTransactionDate] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [outstandingCash, setOutstandingCash] = useState(0);
  // Gross cash deposits for the day. The denomination counts describe THIS,
  // not `total` (which is net of withdrawals) — see validateForm.
  const [grossCashDeposits, setGrossCashDeposits] = useState(0);
  // Online movement, shown separately now that the cash figure excludes it.
  const [onlineTotal, setOnlineTotal] = useState(0);
  // The day's NET cash movement, without the opening balance. `totalCashCollection`
  // is a closing position (opening + movement); this is the movement alone, which
  // is what `cashTotal` records so that a day's activity stays recoverable without
  // re-deriving it from `transactions`.
  const [cashMovement, setCashMovement] = useState(0);
  // Bank opening, read from the bank ledger. Displayed beside the cash box but
  // never counted — a bank balance is verified against a statement, and putting it
  // next to a countable figure is what made cashiers appear short.
  const [bankOpening, setBankOpening] = useState(0);

  // The day's cash and bank movement read from the LEDGER.
  //
  // `transactions.cash_type` cannot answer this: vouchers never write to that
  // table (capital, borrowings and cash-to-bank were all invisible), loan rows
  // carry cash_type NULL so a member's withdrawal was filtered out, and there is
  // no bank-account column so a branch with two banks could not be split.
  //
  // `hasLedgerData` is false for any date before this branch's first ledger
  // entry. Live has 380 day-ends closed before the ledger was in use; for those
  // the screen must keep its old behaviour rather than report zero movement for
  // a day that had plenty.
  const [cashMovementLedger, setCashMovementLedger] = useState(null);
  const [bankMovementLedger, setBankMovementLedger] = useState(null);
  // Guards against the double-click that produced duplicate day-end rows.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outstandingDenominations, setOutstandingDenominations] = useState({
    500: 0,
    200: 0,
    100: 0,
    50: 0,
    20: 0,
    10: 0,
    change: 0,
  });
  const [isStaffCollectionsModalVisible, setIsStaffCollectionsModalVisible] = useState(true);
  const [allApproved, setAllApproved] = useState(false);
  const [approvedCollections, setApprovedCollections] = useState([]);

  // Funding position — lending that has no recorded source behind it.
  //
  // This page shipped without these checks while the old /dayendprocess had
  // them, so a loan disbursed against OWN FUND with no capital receipt closed
  // the day here in silence. The server still refused the write with a 409, but
  // only after the operator had filled in the whole form.
  const [memberBorrowings, setMemberBorrowings] = useState(0);
  // The same figure split by pot, so the block can say WHAT to repay. A
  // repayment clears ONE account, so a single total leaves the operator to
  // discover the second pot only when the day-end refuses a second time.
  const [memberBorrowingsByPot, setMemberBorrowingsByPot] = useState({
    cash: 0, bank: 0, pooled: 0,
  });
  const [savingsShortfall, setSavingsShortfall] = useState(0);
  // Figures behind the liquidity warning (informational; never blocks).
  const [liquidity, setLiquidity] = useState({ savings: 0, liquid: 0 });
  const [unreceiptedAgencies, setUnreceiptedAgencies] = useState([]);
  const [isFundingModalVisible, setIsFundingModalVisible] = useState(true);
  // Which funds-available request is the current question.
  //
  // Three effects can fire fetchFundingPosition — mount, window focus, and the
  // transactionDate change — so on a date change two are often open at once.
  // They finish out of order: the reply for the PREVIOUS date arrived carrying a
  // gap and raised the modal, then the reply for the CURRENT date arrived empty
  // and hid it again. That is the one-second flash the owner reported.
  const fundingReqRef = useRef(0);
  const navigate = useNavigate();
  const { data, roleName, jwt } = useAuth();
  const branchId = data?.branchId || null;
  const isSuperAdmin = roleName === 'superadmin';

  // Helper to format date strictly in IST (Asia/Kolkata)
  const getISTDateString = (dateObj = new Date()) => {
    try {
      const options = { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'numeric', day: 'numeric' };
      const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(dateObj);
      const year = parts.find(p => p.type === 'year').value;
      const month = parts.find(p => p.type === 'month').value.padStart(2, '0');
      const day = parts.find(p => p.type === 'day').value.padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      // Fallback if Intl is not supported
      const tzOffset = 330 * 60000; // 5.5 hours in ms
      const localTime = dateObj.getTime();
      const localOffset = dateObj.getTimezoneOffset() * 60000;
      const istDate = new Date(localTime + localOffset + tzOffset);
      return istDate.toISOString().split('T')[0];
    }
  };

  const getTodayDate = () => getISTDateString(new Date());

  // Sunday is a WORKING day and must be closed like any other — this no longer
  // excludes a date from anything. Kept only to LABEL a Sunday in the list of
  // days still to close, since an operator seeing one there would otherwise
  // assume it was a mistake.
  //
  // Parsed at noon UTC so the IST offset cannot shift the day.
  const isSunday = (dateStr) => {
    const d = new Date(`${dateStr}T12:00:00Z`);
    return d.getUTCDay() === 0; // 0 = Sunday
  };

  // Previous calendar day.
  //
  // This used to skip Sundays, which is why the date strip jumped from Monday
  // straight back to Friday: Sunday 2026-09-06 carried 9 transactions worth
  // Rs.10,05,000 on branch 1 and could never be selected, so that cash never
  // entered the carry-forward chain.
  const getPreviousWorkingDay = (fromDateStr, daysBack = 1) => {
    const date = new Date(`${fromDateStr}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() - daysBack);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Yesterday (calendar day — Sundays included)
  const getYesterdayDate = () => {
    return getPreviousWorkingDay(getTodayDate(), 1);
  };

  // Get two working days before (skips Sundays)
  const getTwoDaysBeforeDate = () => {
    return getPreviousWorkingDay(getTodayDate(), 2);
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/branch_details`);
        setAllBranches(Array.isArray(response.data.data) ? response.data.data : []);
        if (!isSuperAdmin && branchId) {
          setSelectedBranch(branchId);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchBranches();
  }, [isSuperAdmin, branchId]);

  /**
   * What the branch has lent against what it has recorded as funding.
   *
   * Three separate conditions come back from one query, matching the three the
   * server refuses on in controllers/middleware.js:
   *
   *   unreceiptedAgencies  an agency lent more than was ever recorded as received
   *   memberBorrowings     members' savings out on loan, not yet put back
   *   savingsShortfall     put back on paper, but the cash has not returned
   *
   * Read-only and advisory. The server enforces the same rules on submit, so a
   * stale or failed read here cannot let anything through — it only decides
   * whether the operator is warned before filling the form or after.
   *
   * Zeroes on error deliberately: a funding endpoint blip must not lock a branch
   * out of closing its day, since the 409 still stands behind it.
   */
  const fetchFundingPosition = async () => {
    const targetBranch = isSuperAdmin && selectedBranch ? selectedBranch : branchId;
    if (!targetBranch) return;

    // No date, no question. This used to fall back to getTodayDate(), which is
    // how the blocker flashed: at mount the day being closed is not known yet,
    // and TODAY is the one day that carries the gap. Both callers now guard on
    // transactionDate, so this should be unreachable — it is here so that a
    // future caller cannot reintroduce the guess by forgetting the guard.
    if (!transactionDate) return;

    // Claim this request. Only the newest one is allowed to write state.
    //
    // Deliberately NOT AbortController: cancelling fires the catch below, which
    // zeroes every figure — so aborting a superseded request would CLEAR a live
    // blocker instead of leaving the newest answer standing. Discarding the
    // reply is the safe direction.
    const seq = ++fundingReqRef.current;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/ledger/funds-available`, {
        params: {
          branchId: targetBranch,
          // The position AS AT the day being closed, not the branch's position
          // right now. Closing 10/09 was refused because of a loan disbursed on
          // 18/09 — eight days that had not happened yet — and each of Palani's
          // eight open days was blocked by an event none of them could cause.
          //
          // Same expression submitDayEnd uses for `date` (:1561), so the screen
          // and the server-side gate judge the identical day. If they diverge,
          // the button enables while the POST is refused.
          asOfDate: transactionDate,
        },
      });
      // A newer request has been issued since this one left. Its answer is the
      // current one; this reply is about a date the page has already moved past.
      if (seq !== fundingReqRef.current) return;

      const funds = response.data?.data || {};
      setMemberBorrowings(Number(funds.memberBorrowings) || 0);
      setMemberBorrowingsByPot({
        cash: Number(funds.memberBorrowingsCash) || 0,
        bank: Number(funds.memberBorrowingsBank) || 0,
        pooled: Number(funds.memberBorrowingsPooled) || 0,
      });
      setSavingsShortfall(Number(funds.savingsShortfall) || 0);
      setLiquidity({
        savings: Number(funds.memberSavingsLiability) || 0,
        liquid: Number(funds.liquidFunds) || 0,
      });
      setUnreceiptedAgencies(
        Array.isArray(funds.unreceiptedAgencies) ? funds.unreceiptedAgencies : []
      );
    } catch (err) {
      // Guarded for the same reason as the success path, and it matters more
      // here: this branch zeroes every figure, so an OLD request failing after a
      // newer one succeeded would wipe a blocker that genuinely stands.
      if (seq !== fundingReqRef.current) return;

      setMemberBorrowings(0);
      setMemberBorrowingsByPot({ cash: 0, bank: 0, pooled: 0 });
      setSavingsShortfall(0);
      setLiquidity({ savings: 0, liquid: 0 });
      setUnreceiptedAgencies([]);
    }
  };

  const fetchApprovedCollections = async (date) => {
    try {
      let filter = { date, approvedStatus: true };
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/field_officer_collections`, {
        params: { filter: JSON.stringify(filter) },
      });
      const records = Array.isArray(response.data.data) ? response.data.data : [];
      setApprovedCollections(records);
      return records;
    } catch (err) {
      console.error('Error fetching approved collections:', err);
      return [];
    }
  };

  const fetchStaffRecords = async () => {
    setLoadingStaff(true);
    setErrorStaff(null);
    try {
      let filter = {};
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/staff_details`, {
        params: { filter: JSON.stringify(filter) },
      });
      const records = Array.isArray(response.data.data) ? response.data.data : [];
      setStaffRecords(records);
      // Returned as well as stored, like fetchCashRecords and
      // fetchApprovedCollections: a caller that needs the roster in the SAME tick
      // cannot read it back from state, which lags a render behind.
      return records;
    } catch (err) {
      setErrorStaff('Failed to load staff records.');
      setStaffRecords([]);
      return [];
    } finally {
      setLoadingStaff(false);
    }
  };

  const fetchLedgerAccounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/ledger_account?includeDepth=1`);
      const accountsArray = Array.isArray(response.data.data) ? response.data.data : [];
      setLedgerAccounts(accountsArray);
    } catch (error) {
      console.error('Error loading ledger accounts:', error);
      setLedgerAccounts([]);
    }
  };

  const fetchLedgerRecords = async (date) => {
    setLoadingLedger(true);
    setErrorLedger(null);
    try {
      let filter = { date };
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/ledger_transaction`, {
        params: { filter: JSON.stringify(filter) },
      });
      const allRecords = Array.isArray(response.data.data) ? response.data.data : [];

      // The opening-balance entry is the STARTING position, not a movement during
      // the day. It is already applied as the seed in fetchCashRecords, so adding
      // it here as well double-counts it — the symptom being an outstanding figure
      // exactly twice the entered opening balance.
      const records = allRecords.filter(
        (r) => !String(r.particulars || '').startsWith('Opening Balance')
      );
      setLedgerRecords(records);

      // Deliberately does NOT touch outstandingCash.
      //
      // It used to add the net of EVERY ledger row for the day — Loans
      // Outstanding, Fee Income, Borrowings and all — to a figure that means
      // cash in the safe. Two things hid it: a balanced day always nets to zero,
      // and the addition raced fetchCashRecords' own setOutstandingCash with no
      // defined order.
      //
      // The cash position now comes from one place: cashBalance in
      // fetchCashRecords, seeded from the ledger's CASH movement alone.
    } catch (err) {
      setErrorLedger('Failed to load ledger records.');
      setLedgerRecords([]);
    } finally {
      setLoadingLedger(false);
    }
  };

  // `openingBalance` must be passed in by the caller (from fetchYesterdayDayEnd).
  // Reading it from state here is what caused the seed to be 0 on every run.
  const fetchCashRecords = async (date, openingBalance, ledgerMovement = null) => {
    setLoadingCash(true);
    setErrorCash(null);
    const opening = Number(openingBalance) || 0;

    // Set BEFORE the request, not after it.
    //
    // It used to be set further down, inside the try and after an axios call —
    // so a failed transactions fetch left transactionDate null. That was
    // survivable while the funding position was also fetched on mount, but the
    // mount fetch has been removed (it guessed TODAY as the date and raised a
    // blocker for the wrong day). The funding position is now fetched only when
    // this value changes, so leaving it unset on an error would mean the blocker
    // never loads at all.
    //
    // `date` is the caller's argument, already decided. Nothing below can change
    // what day is being closed.
    setTransactionDate(date);

    try {
      let filter = { date };
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/transactions`, {
        params: { filter: JSON.stringify(filter) },
      });
      let records = Array.isArray(response.data.data) ? response.data.data : [];

      // Both channels are kept. Filtering to cash HERE discarded every online row
      // before anything could count it, so Online Total, the ONLINE filter button,
      // the approval modal's Online Collection column and the figure written to
      // field_officer_collections were all permanently zero — a real Rs10,000
      // online deposit was recorded in `transactions` and invisible everywhere.
      //
      // The cash-only rule still applies where it matters: the cash balance, the
      // gross-deposit figure the denomination check validates, and the Cash Box
      // panel each filter for themselves below. Online money can therefore never
      // affect the figure a cashier counts.
      const cashOnly = records.filter((record) => record.cash_type === 'cash');
      const onlineRecords = records.filter((record) => record.cash_type === 'online');

      setCashRecords(records);

      // Shown separately so operators still see total collections after the
      // cash-only change above.
      setOnlineTotal(
        onlineRecords.reduce((sum, r) => {
          const amount = calculateTotal(r) || 0;
          return r.transactionType?.toLowerCase() === 'deposit' ? sum + amount : sum - amount;
        }, 0)
      );

      // Net cash movement, seeded with yesterday's closing balance.
      // The day's closing cash position.
      //
      // From the LEDGER when this branch has one. cashOnly sees only
      // transactions.cash_type='cash', so a cash-to-bank transfer — recorded as a
      // payment_voucher, never a transactions row — was invisible to it. On 08/09
      // that made the day close at 4,000 when 2,000 was in the safe, and because
      // outstandingCash below is derived from the SAME figure, validateForm
      // compared 4,000 against 4,000 and passed. The screen contradicted its own
      // summary box and still submitted.
      //
      // The fallback keeps every pre-ledger date behaving exactly as before —
      // live has 380 closed day-ends with no ledger data behind them.
      const cashBalance = ledgerMovement
        ? Number(opening) + Number(ledgerMovement.net)
        : cashOnly.reduce((sum, record) => {
          const amount = calculateTotal(record) || 0;
          return record.transactionType?.toLowerCase() === 'deposit'
            ? sum + amount
            : record.transactionType?.toLowerCase() === 'withdraw'
              ? sum - amount
              : sum;
        }, opening);

      setOutstandingCash(cashBalance);

      // The closing position minus the opening it was seeded with — the day's own
      // movement, stored as `cashTotal`.
      setCashMovement(cashBalance - opening);

      const depositRecords = cashOnly.filter(
        (r) => r.transactionType?.toLowerCase() === 'deposit'
      );

      // GROSS cash deposits — this is what the denomination counts describe.
      // `total` is a NET figure, so validating notes against `total` can only pass
      // on a day with no withdrawals. The two measure different things.
      setGrossCashDeposits(
        depositRecords.reduce((sum, r) => sum + (calculateTotal(r) || 0), 0)
      );

      const depositDenominations = depositRecords.reduce(
        (acc, r) => ({
          500: acc[500] + (Number(r['500']) || 0),
          200: acc[200] + (Number(r['200']) || 0),
          100: acc[100] + (Number(r['100']) || 0),
          50: acc[50] + (Number(r['50']) || 0),
          20: acc[20] + (Number(r['20']) || 0),
          10: acc[10] + (Number(r['10']) || 0),
          change: acc.change + (Number(r.change) || 0),
        }),
        { 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, change: 0 }
      );

      setOutstandingDenominations(depositDenominations);

      // Prefill the cash fields only when there was cash activity — an online-only
      // day has nothing to count.
      //
      // Any PAST date, not just yesterday/two-days-before: the catch-up now works
      // back through every open day, so the target can be older than two days.
      const isPastDate = date < getTodayDate();
      // ONE branch, for any past date — whether or not cash moved.
      //
      // This used to be two: one for a day with cash rows, one for a day with NO
      // rows at all. A day with activity but no CASH fell between them. 08/09 had
      // six transactions, none of them cash_type='cash', so nothing prefilled
      // totalCashCollection and validateForm rejected the empty string with
      // "Please enter a valid total cash collection." — on a day with nothing to
      // count. The operator could not close it without inventing a figure.
      //
      // cashBalance is the carried-forward position either way, so the two cases
      // never needed separating.
      if (isPastDate) {
        setTotalCashCollection(cashBalance.toString());
        // Denominations describe cash RECEIVED. With no cash rows there is
        // nothing to seed, and depositDenominations is already all-zero — the
        // explicit object states that rather than relying on it.
        setCashDenominations(
          cashOnly.length > 0
            ? depositDenominations
            : { 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0 }
        );
        setChange(String(depositDenominations.change || 0));
        setOutstanding(cashBalance.toString());
      }
      // BOTH channels are returned. The caller passes this straight to
      // calculateStaffCollections, which splits cash from online itself — handing
      // it a cash-only array is what made every staff member's Online Collection
      // read 0.00 in the approval modal.
      //
      // `cashRecords` state has not re-rendered yet at that point, so a caller
      // reading it from state would see the previous date's rows.
      return records;
    } catch (err) {
      setErrorCash('Failed to load cash records.');
      setCashRecords([]);
      setOutstandingCash(opening);
      setGrossCashDeposits(0);
      setOnlineTotal(0);
      setCashMovement(0);
      setOutstandingDenominations({ 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, change: 0 });
      return [];
    } finally {
      setLoadingCash(false);
    }
  };

  const fetchLoanRecords = async (date) => {
    setLoadingLoan(true);
    setErrorLoan(null);
    try {
      // Branch-scoped like every other tab on this page. This request previously
      // carried no params at all, so a branch user saw EVERY branch's lending —
      // branch 1 was shown branch 2's approved loans.
      let filter = {};
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/member_details`, {
        params: { filter: JSON.stringify(filter) },
      });
      const records = Array.isArray(response.data.data) ? response.data.data : [];

      // The date BEING CLOSED, not today. Hardcoding today listed loans approved
      // on 07/09 while the operator was closing 05/09 — every sibling fetch takes
      // the target date, this one did not.
      const targetDate = date || getTodayDate();
      const filteredRecords = records.filter(
        (record) =>
          record.adminStatusUpdatedAt &&
          new Date(record.adminStatusUpdatedAt).toISOString().split('T')[0] === targetDate &&
          record.adminStatus === 'submitted'
      );
      setLoanRecords(filteredRecords);
    } catch (err) {
      setErrorLoan('Failed to load loan records.');
      setLoanRecords([]);
    } finally {
      setLoadingLoan(false);
    }
  };

  // Opening balance for `date` = the closing balance of the previous working day.
  // Returns 0 when no prior day-end exists, which is the correct starting point
  // for a branch that has not yet entered a physically counted figure.
  // Opening balance for `date`, read from the CASH LEDGER.
  //
  // The ledger is authoritative, not day_end_processes.outstanding: that column
  // held each day's net movement rather than a balance, and 156 of its 378 rows
  // are clones produced by a manual INSERT..SELECT. Reading the ledger means a
  // wrong figure is corrected by editing one receipt, instead of propagating
  // silently down a recomputed chain.
  //
  // Falls back to the old column only when no ledger account exists yet, so the
  // page keeps working before scripts/create_branch_ledger_accounts.js is run.
  // The day's movement in one channel, from the ledger. Returns null when this
  // branch has no ledger data for the date, so the caller falls back rather than
  // showing a misleading zero.
  const fetchLedgerMovement = async (date, kind) => {
    const effectiveBranch = !isSuperAdmin ? branchId : (selectedBranch || branchId);
    if (!effectiveBranch) return null;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/ledger/movement`, {
        params: { branchId: effectiveBranch, date, kind },
      });
      const data = response.data?.data;
      return data && data.hasLedgerData ? data : null;
    } catch (err) {
      // A movement we cannot read must not block the close. The screen falls
      // back to the transactions-derived figure, which is what it used before.
      console.error('Ledger movement unavailable:', err);
      return null;
    }
  };

  const fetchOpeningBalanceFor = async (date, kind = 'cash') => {
    const effectiveBranch = !isSuperAdmin ? branchId : (selectedBranch || branchId);
    if (!effectiveBranch) return 0;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/ledger/opening-balance`, {
        params: { branchId: effectiveBranch, date, kind },
      });
      const data = response.data?.data;
      // `found`, not `accountId`. Asked without an accountId the endpoint sums
      // the whole Cash In Hand / Bank Accounts group and returns accountId null,
      // because there is no single account — testing accountId here would read
      // that as "no ledger" and fall through to the legacy chain.
      if (data && data.found) {
        return Number(data.balance) || 0;
      }
      console.warn('No ' + kind + ' ledger account for branch ' + effectiveBranch + '.');
    } catch (err) {
      console.error('Ledger opening balance unavailable:', err);
    }

    // Only cash has a pre-ledger fallback — day_end_processes never held a bank
    // figure, so there is nothing to fall back to for the bank chain.
    return kind === 'cash' ? fetchLegacyOpeningBalance(date) : 0;
  };

  // Pre-ledger behaviour, retained as a fallback only.
  const fetchLegacyOpeningBalance = async (date) => {
    try {
      const previous = getPreviousWorkingDay(date, 1);
      let filter = { date: previous };
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/DayEndProcess`, {
        params: { filter: JSON.stringify(filter) },
      });
      const records = Array.isArray(response.data.data) ? response.data.data : [];
      const record = records.find(
        (r) =>
          r.date &&
          new Date(r.date).toISOString().split('T')[0] === previous &&
          (!filter.branchId || r.branchId === filter.branchId)
      );
      return Number(record?.outstanding) || 0;
    } catch (err) {
      console.error('Error fetching opening balance for ' + date + ':', err);
      return 0;
    }
  };

  // Returns the opening balance as well as setting it in state.
  // The return value is what callers must use: React state is not readable by
  // fetchCashRecords in the same tick, which is why the running balance used to
  // seed from 0 on every run and the day-end recorded movement instead of cash.
  const fetchYesterdayDayEnd = async () => {
    try {
      const yesterday = getYesterdayDate();
      let filter = { date: yesterday };
      if (!isSuperAdmin || (isSuperAdmin && selectedBranch)) {
        filter.branchId = !isSuperAdmin ? branchId : selectedBranch;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/DayEndProcess`, {
        params: { filter: JSON.stringify(filter) },
      });
      const records = Array.isArray(response.data.data) ? response.data.data : [];
      const yesterdayRecord = records.find(
        (record) =>
          record.date &&
          new Date(record.date).toISOString().split('T')[0] === yesterday &&
          (!filter.branchId || record.branchId === filter.branchId)
      );
      const opening = Number(yesterdayRecord?.outstanding) || 0;
      setYesterdayOutstanding(opening);
      return opening;
    } catch (err) {
      console.error('Error fetching yesterday day-end:', err);
      setYesterdayOutstanding(0);
      return 0;
    }
  };

  const fetchDayEndStatus = async () => {
    setLoadingDayEndStatus(true);
    try {
      const today = getTodayDate();
      const yesterday = getYesterdayDate();
      const twoDaysBefore = getTwoDaysBeforeDate();

      console.log("========== DAYEND: fetchDayEndStatus START ==========");
      console.log("Dates:", { today, yesterday, twoDaysBefore });
      console.log("BranchId:", branchId, "| isSuperAdmin:", isSuperAdmin, "| selectedBranch:", selectedBranch);

      let filter = {};
      if (!isSuperAdmin) {
        filter.branchId = branchId;
      } else if (selectedBranch) {
        filter.branchId = selectedBranch;
      }
      console.log("Filter:", JSON.stringify(filter));

      const twoDaysBeforeTransactionsResponse = await axios.get(`${API_BASE_URL}/api/getModelData/transactions`, {
        params: { filter: JSON.stringify({ ...filter, date: twoDaysBefore }) },
      });

      const yesterdayTransactionsResponse = await axios.get(`${API_BASE_URL}/api/getModelData/transactions`, {
        params: { filter: JSON.stringify({ ...filter, date: yesterday }) },
      });

      const hasTwoDaysBeforeTransactions =
        Array.isArray(twoDaysBeforeTransactionsResponse.data.data) &&
        twoDaysBeforeTransactionsResponse.data.data.some(
          (record) => !record.cash_type || record.cash_type === 'cash' || record.cash_type === 'online'
        );

      const hasYesterdayTransactions =
        Array.isArray(yesterdayTransactionsResponse.data.data) &&
        yesterdayTransactionsResponse.data.data.some(
          (record) => !record.cash_type || record.cash_type === 'cash' || record.cash_type === 'online'
        );

      console.log("Transaction existence:", {
        twoDaysBefore: hasTwoDaysBeforeTransactions,
        twoDaysBeforeCount: twoDaysBeforeTransactionsResponse.data?.data?.length || 0,
        yesterday: hasYesterdayTransactions,
        yesterdayCount: yesterdayTransactionsResponse.data?.data?.length || 0
      });

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/DayEndProcess`, {
        params: { filter: JSON.stringify(filter) },
      });
      const records = Array.isArray(response.data.data) ? response.data.data : [];
      console.log("DayEndProcess total records returned:", records.length);

      const todayRecord = records.find(
        (record) =>
          record.date &&
          new Date(record.date).toISOString().split('T')[0] === today &&
          (!filter.branchId || record.branchId === filter.branchId)
      );
      const yesterdayRecord = records.find(
        (record) =>
          record.date &&
          new Date(record.date).toISOString().split('T')[0] === yesterday &&
          (!filter.branchId || record.branchId === filter.branchId)
      );
      const twoDaysBeforeRecord = records.find(
        (record) =>
          record.date &&
          new Date(record.date).toISOString().split('T')[0] === twoDaysBefore &&
          (!filter.branchId || record.branchId === filter.branchId)
      );

      console.log("Record matching results:", {
        todayRecord: todayRecord ? { id: todayRecord.id, depstatus: todayRecord.depstatus, date: todayRecord.date } : 'NOT FOUND',
        yesterdayRecord: yesterdayRecord ? { id: yesterdayRecord.id, depstatus: yesterdayRecord.depstatus, date: yesterdayRecord.date } : 'NOT FOUND',
        twoDaysBeforeRecord: twoDaysBeforeRecord ? { id: twoDaysBeforeRecord.id, depstatus: twoDaysBeforeRecord.depstatus, date: twoDaysBeforeRecord.date } : 'NOT FOUND'
      });

      setTodaysDayEndRecord(todayRecord || null);
      setDayEndStatus(todayRecord?.depstatus || 'pending');
      setYesterdayDayEndStatus(yesterdayRecord?.depstatus || 'pending');
      setTwoDaysBeforeDayEndStatus(twoDaysBeforeRecord?.depstatus || 'pending');

      // The authoritative list of open dates. Asking the server means this page,
      // the old day-end screen and the sidebar lock cannot disagree about what is
      // outstanding, and the cutover date (DAYEND_ENFORCE_FROM) lives in one place.
      let dayEndCheck = null;
      try {
        const checkResponse = await axios.get(`${API_BASE_URL}/api/checkYesterdayDayEnd`, {
          params: filter.branchId ? { branchId: filter.branchId } : {},
        });
        dayEndCheck = checkResponse.data?.data || null;
        console.log("Open dates from server:", dayEndCheck?.openDates);
      } catch (err) {
        // Fail open, matching Sidebar.jsx: a backend blip must not strand the
        // operator on a page that cannot pick a date.
        console.error("checkYesterdayDayEnd failed; falling back to today.", err);
      }

      const serverOpenDates = Array.isArray(dayEndCheck?.openDates)
        ? dayEndCheck.openDates
        : [];
      setPendingDates(serverOpenDates);

      // Opening balances read from the cash ledger, one per candidate date.
      // Held in locals so they can be passed to fetchCashRecords directly —
      // reading them back from state in the same tick is the original bug.
      const [todayOpening, yesterdayOpening, twoDaysBeforeOpening] = await Promise.all([
        fetchOpeningBalanceFor(today),
        fetchOpeningBalanceFor(yesterday),
        fetchOpeningBalanceFor(twoDaysBefore),
      ]);
      setYesterdayOutstanding(todayOpening);

      console.log("State updates:", {
        dayEndStatus: todayRecord?.depstatus || 'pending',
        yesterdayDayEndStatus: yesterdayRecord?.depstatus || 'pending',
        twoDaysBeforeDayEndStatus: twoDaysBeforeRecord?.depstatus || 'pending',
        todayOpening,
        yesterdayOpening,
        twoDaysBeforeOpening,
      });

      setShowDayEnd(!todayRecord || todayRecord.depstatus !== 'submitted');

      if (todayRecord?.depstatus === 'submitted' &&
        (!yesterdayRecord || yesterdayRecord.depstatus === 'submitted') &&
        (!twoDaysBeforeRecord || twoDaysBeforeRecord.depstatus === 'submitted')) {
        console.log("All days submitted → showing today's submitted data");
        setTotalCashCollection(Number(todayRecord.total || 0).toString());
        setCashDenominations({
          500: Number(todayRecord.denomination_500) || 0,
          200: Number(todayRecord.denomination_200) || 0,
          100: Number(todayRecord.denomination_100) || 0,
          50: Number(todayRecord.denomination_50) || 0,
          20: Number(todayRecord.denomination_20) || 0,
          10: Number(todayRecord.denomination_10) || 0,
        });
        setChange(Number(todayRecord.change || 0).toString());
        setOutstanding(Number(todayRecord.outstanding || 0).toString());
        setExpenses(Number(todayRecord.expenses || 0).toString());
        setBranchPayout(Number(todayRecord.branchpayout || 0).toString());
        setBranches(todayRecord.branches || '');
      } else {
        console.log("Some days NOT submitted → clearing form fields");
        setTotalCashCollection('');
        setCashDenominations({ 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0 });
        setChange('');
        setOutstanding('');
        setExpenses('');
        setBranchPayout('');
        setBranches('');
      }

      // The approval check runs AFTER the decision tree below, once targetDate is
      // known and fetchCashRecords has loaded that date's transactions. Checking
      // here read `transactionDate`, which is still null on first load, so it fell
      // back to today and asked "is there an approval dated today?" — while the
      // collection being approved is dated whatever day is actually being closed.
      // The answer was always no, so the modal reopened every morning.

      // Decision tree for which date's transactions to show.
      // Each opening balance above is already the ledger balance as at that date
      // (entries strictly before it), so the target date selects its own opening
      // directly — no offsetting by a day.
      const openingByDate = {
        [today]: todayOpening,
        [yesterday]: yesterdayOpening,
        [twoDaysBefore]: twoDaysBeforeOpening,
      };
      // Which date to close, from the server's list of every open day.
      //
      // This replaced a tree that only ever considered today / yesterday /
      // two-days-before, while the backend blocks back to the last submitted date
      // with no limit — so three missed days had no way out of the UI. The days
      // must also be closed OLDEST FIRST, because each day's `outstanding` is the
      // next day's opening balance.
      const targetDate = serverOpenDates.length > 0 ? serverOpenDates[0] : today;

      if (serverOpenDates.length > 0) {
        console.log(`DECISION: ${serverOpenDates.length} open date(s); closing oldest → ${targetDate}`);
        setFormError(
          serverOpenDates.length === 1
            ? `Day-end for ${formatDate(targetDate)} is pending. Please complete it before proceeding.`
            : `Day-end is pending for ${serverOpenDates.length} days. Closing ${formatDate(targetDate)} first — they must be completed in order.`
        );
      } else {
        console.log("DECISION: nothing open → show TODAY");
      }

      // Only today / yesterday / two-days-before were pre-fetched above. An older
      // catch-up date fetches its own opening balance.
      const targetOpening =
        openingByDate[targetDate] ?? (await fetchOpeningBalanceFor(targetDate));
      setYesterdayOutstanding(targetOpening);
      console.log("Opening balance for " + targetDate + " = " + targetOpening);
      // The day's CASH movement from the ledger, fetched BEFORE the cash records
      // because fetchCashRecords seeds the closing position from it. Passed as an
      // argument, not read from state: state lags a render behind, the same trap
      // already documented for staffRecords and approvedCollections below.
      const cashMv = await fetchLedgerMovement(targetDate, 'cash');
      setCashMovementLedger(cashMv);
      const targetCashRecords = await fetchCashRecords(targetDate, targetOpening, cashMv);
      await fetchLedgerRecords(targetDate);
      // Same target date as the tabs above, so Loans cannot drift to today.
      await fetchLoanRecords(targetDate);

      // Bank opening for the same date. Fetched here rather than alongside the cash
      // openings above because only the target date's bank figure is displayed.
      setBankOpening(await fetchOpeningBalanceFor(targetDate, 'bank'));

      // Bank movement, for the Online / Bank box. The CASH half is fetched
      // earlier, above, because fetchCashRecords needs it to seed the day's
      // closing position.
      setBankMovementLedger(await fetchLedgerMovement(targetDate, 'bank'));

      // Approvals are checked for the date actually being closed, and against the
      // arrays just fetched rather than component state — neither `cashRecords` nor
      // `approvedCollections` has re-rendered at this point, so reading state here
      // sees the previous date's data.
      // The roster is fetched HERE and passed in, rather than relying on the
      // parallel fetchStaffRecords() to have finished. Without this the blocker
      // appeared or not depending on which request won the race.
      const staffList = await fetchStaffRecords();
      const approvedRecords = await fetchApprovedCollections(targetDate);
      const staffCollections = calculateStaffCollections(
        approvedRecords,
        targetCashRecords,
        staffList
      );
      const allCollectionsApproved = staffCollections.every((staff) =>
        approvedRecords.some((record) => record.staffId === staff.id && record.date === staff.date)
      );
      setAllApproved(allCollectionsApproved);
      setIsStaffCollectionsModalVisible(!allCollectionsApproved && staffCollections.length > 0);
    } catch (err) {
      console.error("fetchDayEndStatus ERROR:", err);
      setFormError("Failed to load day-end status. Showing today's transactions.");
      setDayEndStatus('pending');
      setYesterdayDayEndStatus('pending');
      setTwoDaysBeforeDayEndStatus('pending');
      setYesterdayOutstanding(0);
      await fetchCashRecords(getTodayDate(), 0);
      await fetchLedgerRecords(getTodayDate());
    } finally {
      setLoadingDayEndStatus(false);
    }
  };

  useEffect(() => {
    if (branchId) {
      fetchYesterdayDayEnd();
      // fetchLoanRecords and fetchStaffRecords are NOT called here.
      //
      // fetchLoanRecords needs the date being closed, which is only resolved
      // inside fetchDayEndStatus — calling it here passed no date, so it fell
      // back to today and listed loans from the wrong day.
      //
      // fetchStaffRecords ran here IN PARALLEL with fetchDayEndStatus, which
      // reads the roster to decide whether the staff-collections blocker opens.
      // Whichever finished first won, so the blocker appeared only sometimes.
      // fetchDayEndStatus now awaits it, like the cash and approval fetches.
      fetchDayEndStatus();
      fetchLedgerAccounts();
      // fetchFundingPosition is NOT called here.
      //
      // At mount transactionDate is null, so it fell back to getTodayDate() and
      // asked for TODAY's position — which is the one day that has a gap. The
      // modal appeared for a second, then fetchDayEndStatus resolved, the date
      // became 15/09, the second fetch returned no gap and it vanished.
      //
      // Both answers were correct; the first question was the wrong one. The
      // transactionDate effect below asks it once the day being closed is known.
    }
  }, [branchId, selectedBranch]);

  // Refetch the funding position whenever the day being CLOSED changes.
  //
  // Its own effect, NOT a dependency added to the one above. fetchDayEndStatus
  // there calls fetchCashRecords, which SETS transactionDate (:446) — so adding
  // it to that array makes the effect retrigger itself. React bails out once the
  // value stops changing, but a self-feeding effect is not something to leave
  // resting on that.
  //
  // This one only reads the date and fetches, so it cannot feed itself.
  useEffect(() => {
    if (branchId && transactionDate) fetchFundingPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionDate]);

  // A dismissal belongs to the day it was read on.
  //
  // Both flags start true and were only ever set FALSE — a per-day decision
  // stored as a per-session one. Press x on 14/09, where there is nothing to
  // read, and the Rs.1,00,000 blocker on 18/09 never appears: Submit is
  // correctly disabled, with nothing on screen saying why. The books stay safe
  // (validateForm still gates Submit) but the operator is left guessing.
  //
  // Resetting here asks the question again for each new day. Dismissal still
  // works WITHIN a day, which is what keeps the page usable while a gap stands.
  //
  // FUNDING ONLY. The staff modal already re-opens at :980 and :1808, and does it
  // CONDITIONALLY — only when collections are actually unapproved. Adding a
  // blanket `true` here would pop it on a day with nothing to approve, and the
  // comment at :1202 records what happens when this modal is reopened too
  // eagerly: the X became unusable and the operator could not leave the page to
  // check a collection before approving it.
  useEffect(() => {
    setIsFundingModalVisible(true);
  }, [transactionDate]);

  // Recheck funding when the operator comes back to this tab. Clearing the block
  // means leaving for Ledger → Receipts and returning; without this the popup
  // would still be up on a receipt that has already been entered, and the only
  // way past it would be a page reload.
  //
  // transactionDate is in the deps because fetchFundingPosition now sends it as
  // asOfDate, and this listener CLOSES OVER it. Without it the handler keeps the
  // date from the render that registered it, so moving the strip to the next day
  // and returning to the tab would refetch the position for the PREVIOUS day —
  // showing a block that has been cleared, or clearing one that still stands.
  useEffect(() => {
    const onFocus = () => {
      // transactionDate guarded here too: without it, returning to the tab
      // before the day is known would ask for TODAY and flash the blocker for
      // the wrong day — the same fault the mount fetch had.
      if (branchId && transactionDate) fetchFundingPosition();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, selectedBranch, transactionDate]);

  const calculateTotal = (record) => {
    if (!record) return 0;
    const denominationsTotal =
      (Number(record['500']) || 0) * 500 +
      (Number(record['200']) || 0) * 200 +
      (Number(record['100']) || 0) * 100 +
      (Number(record['50']) || 0) * 50 +
      (Number(record['20']) || 0) * 20 +
      (Number(record['10']) || 0) * 10 +
      (Number(record.change) || 0);
    return denominationsTotal > 0 ? denominationsTotal : Number(record.amount) || 0;
  };

  const calculateCashTotals = (records) => {
    if (!Array.isArray(records)) {
      return {
        total: 0,
        depositTotal: 0,
        withdrawTotal: 0,
        cashTotal: 0,
        onlineTotal: 0,
        500: 0,
        200: 0,
        100: 0,
        50: 0,
        20: 0,
        10: 0,
        change: 0,
      };
    }

    return records.reduce(
      (acc, record) => {
        const amount = calculateTotal(record);

        // Deposits and withdrawals must NOT be added together.
        //
        // `acc.total` did exactly that, and reported Rs.10,05,000 for a day of
        // Rs.5,05,000 in and Rs.5,00,000 out — a figure describing nothing. It is
        // kept only because the label reads "Total"; the meaningful numbers are
        // depositTotal, withdrawTotal and their difference.
        const isWithdraw = String(record.transactionType || '').toLowerCase() === 'withdraw';
        if (isWithdraw) acc.withdrawTotal += amount;
        else acc.depositTotal += amount;

        acc.total += amount;
        if (record.cash_type === 'cash') {
          acc.cashTotal += amount;
          acc[500] += Number(record['500']) || 0;
          acc[200] += Number(record['200']) || 0;
          acc[100] += Number(record['100']) || 0;
          acc[50] += Number(record['50']) || 0;
          acc[20] += Number(record['20']) || 0;
          acc[10] += Number(record['10']) || 0;
          acc.change += Number(record.change) || 0;
        } else if (record.cash_type === 'online') {
          acc.onlineTotal += amount;
        }
        return acc;
      },
      {
        total: 0,
        depositTotal: 0,
        withdrawTotal: 0,
        cashTotal: 0,
        onlineTotal: 0,
        500: 0,
        200: 0,
        100: 0,
        50: 0,
        20: 0,
        10: 0,
        change: 0,
      }
    );
  };

  // Both arrays default to state for the render-time call sites, but a caller that
  // has just fetched them must pass its own — state lags a render behind, and that
  // lag is what made the approval check compare against the wrong date's data.
  const calculateStaffCollections = (
    approvedList = approvedCollections,
    cashList = cashRecords,
    // The roster is a PARAMETER for the same reason the other two are. It used to
    // be read from `staffRecords` state while fetchStaffRecords ran in parallel
    // with fetchDayEndStatus, so whether the blocker appeared depended on which
    // request returned first: roster first -> collections found -> modal opens;
    // day-end first -> roster still [] -> length 0 -> no modal. That is the
    // "sometimes it shows, sometimes it doesn't" on reload.
    staffList = staffRecords
  ) => {
    if (!Array.isArray(staffList) || !Array.isArray(cashList)) return [];
    const approvals = Array.isArray(approvedList) ? approvedList : [];

    return staffList
      .map((staff) => {
        const staffTransactions = cashList.filter(
          (record) => record.managerId === staff.id && record.transactionType?.toLowerCase() === 'deposit'
        );

        const collections = staffTransactions.reduce(
          (acc, record) => {
            const amount = calculateTotal(record);
            if (record.cash_type === 'cash') {
              acc.cash += amount;
            } else if (record.cash_type === 'online') {
              acc.online += amount;
            }
            return acc;
          },
          { cash: 0, online: 0 }
        );

        const isApproved = approvals.some(
          (record) => record.staffId === staff.id && record.date === (staffTransactions.length > 0 ? staffTransactions[0].date : getTodayDate())
        );

        return {
          id: staff.id,
          name: staff.name || 'N/A',
          cashCollection: collections.cash,
          onlineCollection: collections.online,
          totalCollection: collections.cash + collections.online,
          date: staffTransactions.length > 0 ? staffTransactions[0].date : getTodayDate(),
          approvedStatus: isApproved,
          // The rows behind the total, so the modal can show WHICH deposits make
          // it up. Without this the popup states Rs.16,000 with no way to check
          // it, and an operator who can only see two rows in the table below
          // reasonably concludes a deposit has gone missing.
          breakdown: staffTransactions.map((r) => ({
            id: r.id,
            amount: calculateTotal(r),
            channel: r.cash_type === 'cash' ? 'cash' : 'online',
          })),
        };
      })
      .filter((staff) => staff.cashCollection > 0 || staff.onlineCollection > 0);
  };

  const staffCollections = calculateStaffCollections();

  // Approval state must stay live — the submit gate reads it on every keystroke.
  //
  // The MODAL's visibility is deliberately NOT set here. calculateStaffCollections
  // ends in .map().filter(), so `staffCollections` is a brand-new array on every
  // render; useEffect compares deps by reference, so this fires constantly. It
  // used to call setIsStaffCollectionsModalVisible(true) whenever anything was
  // unapproved, which re-opened the modal the instant the operator dismissed it —
  // the X was unusable, and there was no way to go and check a collection against
  // a report before approving it.
  //
  // Opening is handled where the data loads instead (fetchDayEndStatus and the
  // approve handler), which runs once per day-load, so the modal still appears
  // for a day with pending approvals. Dismissing it hides a reminder, not the
  // rule: validateForm still refuses to submit while anything is unapproved.
  useEffect(() => {
    setAllApproved(staffCollections.every((staff) => staff.approvedStatus));
  }, [staffCollections]);

  const filteredCashRecords = Array.isArray(cashRecords)
    ? cashRecords.filter((record) => {
      if (!record) return false;
      const type = record.transactionType?.toLowerCase() || 'N/A';
      // Matches what the row DISPLAYS: anything that is not cash reads as
      // online, including the NULL-channel loan rows. Without this the Online
      // filter would hide rows the table labels "online", which reads as a bug.
      const cashType = record.cash_type === 'cash' ? 'cash' : 'online';
      const typeMatch = filterType === 'All' || type === filterType.toLowerCase();
      const cashTypeMatch =
        transactionFilter === 'All' ||
        (transactionFilter === 'Cash' && cashType === 'cash') ||
        (transactionFilter === 'Online' && cashType === 'online');
      return typeMatch && cashTypeMatch;
    })
    : [];

  // ── Ledger tab: raw double-entry rows -> one line per movement ─────────────
  //
  // ledger_transaction stores every event TWICE — once from each account's point
  // of view, with the columns mirrored:
  //
  //   Bank    -> Savings   receipt   1,000    Member deposit
  //   Savings -> Bank      payment   1,000    Member deposit
  //
  // That is correct double entry and the database must keep both (the Ledger
  // Report, Balance Sheet and the day-end balance check all read them). But an
  // operator closing a day sees 20 rows for 10 events and cannot read it.
  //
  // These helpers collapse the pairs for DISPLAY ONLY. Nothing is written.

  // ledger_transaction has no "deposit"/"withdraw" column — that word lives on
  // the source transactions row, and vouchers have no equivalent at all. So the
  // type is read from the narration prefix, the same coupling isLoanDisbursement
  // and DEDUCTION_RULES already rely on in ledgerPosting.js.
  //
  // A reworded narration shows "Movement" rather than silently mislabelling, so
  // the failure is visible.
  const LEDGER_TYPES = [
    ['Member deposit', 'Deposit'],
    ['Withdrawal', 'Withdrawal'],
    ['Loan disbursement', 'Disbursement'],
    ['Processing Fee deduction', 'Fee'],
    ['GST deduction', 'GST'],
    ['Insurance deduction', 'Insurance'],
    ['Security Deposit deduction', 'Security Deposit'],
    ['Capital received', 'Capital'],
    ['Borrowing received', 'Borrowing'],
    ['Agency repayment', 'Agency Repayment'],
    ["Members' savings repaid", 'Savings Repaid'],
    ['Cash deposited to bank', 'Cash to Bank'],
  ];
  const ledgerTypeOf = (particulars) => {
    const p = String(particulars || '');
    const hit = LEDGER_TYPES.find(([prefix]) => p.startsWith(prefix));
    return hit ? hit[1] : 'Movement';
  };

  // Colour carries meaning, not decoration: money arriving is green, money
  // leaving is red, and everything internal (a fee, a transfer between the
  // FPO's own accounts) is neutral because no cash crossed the counter.
  const LEDGER_TYPE_TONE = {
    Deposit: 'success',
    Capital: 'success',
    Borrowing: 'success',
    Withdrawal: 'danger',
    Disbursement: 'danger',
    'Agency Repayment': 'danger',
    'Savings Repaid': 'danger',
    Fee: 'info',
    GST: 'warning',
    Insurance: 'warning',
    'Security Deposit': 'warning',
  };

  const accountNameOf = (id) =>
    ledgerAccounts.find((a) => a.id === id)?.name || 'Unknown';

  const ledgerLines = useMemo(() => {
    if (!Array.isArray(ledgerRecords)) return [];

    // 1. Group the rows back into the events that wrote them.
    const events = new Map();
    ledgerRecords.forEach((r) => {
      if (!r) return;
      // Rows with no source (hand-inserted, or legacy) cannot be paired, so each
      // stands alone rather than being silently dropped.
      const key = r.sourceModel && r.sourceId
        ? `${r.sourceModel}#${r.sourceId}`
        : `row#${r.id}`;
      if (!events.has(key)) events.set(key, []);
      events.get(key).push(r);
    });

    // 2. From each event keep the side that carries the detail.
    //
    //    A plain pair (1 debit / 1 credit) keeps the PAYMENT row, because its
    //    accountId is where the money came FROM and destination_ledger is where
    //    it went TO — a member deposit then reads "Savings -> Bank".
    //
    //    A SPLIT keeps the many side instead. An EMI is 1 debit / 2 credits
    //    (principal + interest); a split-funded loan and an agency repayment with
    //    interest are 2 debits / 1 credit. Always keeping the payment row would
    //    collapse a two-agency loan into a single line and hide the split.
    const kept = [];
    events.forEach((rows) => {
      const receipts = rows.filter((r) => r.transaction_type?.toLowerCase() === 'receipt');
      const payments = rows.filter((r) => r.transaction_type?.toLowerCase() === 'payment');
      const detail = receipts.length > payments.length ? receipts
        : payments.length > 0 ? payments
          : rows;

      detail.forEach((r) => {
        // A receipt row is stored the other way round, so its source and
        // destination swap to keep every line reading source -> destination.
        const isReceipt = r.transaction_type?.toLowerCase() === 'receipt';
        kept.push({
          id: r.id,
          date: r.date,
          sourceId: isReceipt ? r.destination_ledger : r.accountId,
          destId: isReceipt ? r.accountId : r.destination_ledger,
          type: ledgerTypeOf(r.particulars),
          amount: Number(r.amount || 0),
          particulars: r.particulars || 'N/A',
          count: 1,
        });
      });
    });

    // 3. Roll up repeated collections. Only lines on the SAME route with the
    //    same type merge, so no amount is double-counted — three members paying
    //    cash into savings become one line showing the total and "(x3)".
    //
    //    Deliberately NOT merged: everything else. A disbursement, a fee or a
    //    capital receipt is a distinct event the operator needs to see on its
    //    own; only routine member collections repeat enough to be worth rolling
    //    up.
    const GROUPABLE = new Set(['Deposit', 'Withdrawal']);
    const merged = [];
    const byRoute = new Map();
    kept.forEach((line) => {
      if (!GROUPABLE.has(line.type)) { merged.push(line); return; }
      const key = `${line.sourceId}|${line.destId}|${line.type}`;
      const existing = byRoute.get(key);
      if (existing) {
        existing.amount += line.amount;
        existing.count += 1;
        // The narration named one member's payment; the line now covers several.
        existing.particulars = `${line.type} — ${existing.count} collections`;
      } else {
        const copy = { ...line };
        byRoute.set(key, copy);
        merged.push(copy);
      }
    });

    return merged.sort((a, b) => a.id - b.id);
  }, [ledgerRecords, ledgerAccounts]);

  const filteredLedgerRecords = ledgerLines.filter((line) => {
    const typeMatch = ledgerTypeFilter === 'All' || line.type === ledgerTypeFilter;
    const accountMatch = ledgerAccountFilter === 'All'
      || line.sourceId === parseInt(ledgerAccountFilter)
      || line.destId === parseInt(ledgerAccountFilter);
    return typeMatch && accountMatch;
  });

  // The types actually present today, so the filter never offers an empty option.
  const ledgerTypeOptions = ['All', ...Array.from(new Set(ledgerLines.map((l) => l.type)))];

  // Money in, money out, and the net — counting ONLY the lines where cash or
  // bank actually moved.
  //
  // A plain sum of every line is meaningless, and was wrong here before: it
  // reported Rs.15,05,000 for a day whose real movement was Rs.26,800. The
  // Rs.5,00,000 disbursement and the four fee deductions never touched cash or
  // bank at all — they shift value between the FPO's own liability accounts —
  // so adding them to genuine receipts produces a figure describing nothing.
  //
  // This is the figure the denomination count has to reconcile against, so it
  // must mean exactly "what came in and what went out today".
  const isCashOrBank = (id) => {
    const code = ledgerAccounts.find((a) => a.id === id)?.account_code;
    return code === 'CASH' || code === 'BANK';
  };

  const ledgerTotals = filteredLedgerRecords.reduce(
    (acc, l) => {
      // Money arrived if cash/bank is the DESTINATION; it left if cash/bank is
      // the SOURCE. A line touching neither is internal and counts for nothing.
      if (isCashOrBank(l.destId)) acc.moneyIn += l.amount;
      else if (isCashOrBank(l.sourceId)) acc.moneyOut += l.amount;
      else acc.internal += l.amount;
      return acc;
    },
    { moneyIn: 0, moneyOut: 0, internal: 0 }
  );
  ledgerTotals.net = ledgerTotals.moneyIn - ledgerTotals.moneyOut;

  // Two sets of totals, deliberately different:
  //   cashTotals — the rows currently visible, so the Total row matches the table.
  //   dayTotals  — the whole day, so the Cash/Online summary lines describe the day
  //                rather than the filter. Selecting CASH must not make the day's
  //                online collection appear to be zero.
  const cashTotals = calculateCashTotals(filteredCashRecords);
  const dayTotals = calculateCashTotals(cashRecords);

  // Row counts for the totals rows. Both follow the filter buttons, like the
  // money beside them — a count that ignored the filters would contradict the
  // rows actually on screen.
  const depositRowCount = filteredCashRecords.filter(
    (r) => r?.transactionType?.toLowerCase() === 'deposit'
  ).length;
  const withdrawRowCount = filteredCashRecords.filter(
    (r) => r?.transactionType?.toLowerCase() === 'withdraw'
  ).length;

  // Movement lines follow the filter buttons; the Expected figures below do not.
  // Reading these from the unfiltered records meant selecting WITHDRAW still showed
  // the day's deposits, so the boxes contradicted the table directly above them.
  const totalDepositAmount = filteredCashRecords
    .filter((record) => record?.transactionType?.toLowerCase() === 'deposit' && record.cash_type === 'cash')
    .reduce((sum, record) => sum + (calculateTotal(record) || 0), 0);

  const totalWithdrawalAmount = filteredCashRecords
    .filter((record) => record?.transactionType?.toLowerCase() === 'withdraw' && record.cash_type === 'cash')
    .reduce((sum, record) => sum + (calculateTotal(record) || 0), 0);

  // Online movement under the current filter. `onlineTotal` stays whole-day and
  // feeds Expected Bank Balance, which must not move when a filter is clicked.
  const filteredOnlineMovement = filteredCashRecords
    .filter((record) => record.cash_type === 'online')
    .reduce((sum, record) => {
      const amount = calculateTotal(record) || 0;
      return record.transactionType?.toLowerCase() === 'deposit' ? sum + amount : sum - amount;
    }, 0);

  // Which summary box applies to the channel currently selected. Hiding rather than
  // zeroing: a zeroed Online Box reads as "no online money today", which is the
  // misreading that hid a real Rs10,000 deposit for two days.
  const showCashBox = transactionFilter !== 'Online';
  const showOnlineBox = transactionFilter !== 'Cash';

  const getTransactionType = (record) => {
    return record?.transactionType
      ? record.transactionType.charAt(0).toUpperCase() + record.transactionType.slice(1).toLowerCase()
      : 'N/A';
  };

  // en-GB, not the bare call: with no locale the BROWSER decides, so a machine set
  // to US English rendered 5 September as 9/5/2026 — a real ambiguity, since the
  // same screen could read differently on two machines in one branch. en-GB is
  // always zero-padded DD/MM/YYYY. (Plain 'en-IN' gives the right order but no
  // padding: 5/9/2026.)
  //
  // Display only — dates are still sent and stored as YYYY-MM-DD.
  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString('en-GB') : 'N/A';
  };

  const calculateLoanTotals = () => {
    if (!Array.isArray(loanRecords)) return { amountToRelease: 0, gst: 0, processingCharge: 0 };
    return loanRecords.reduce(
      (acc, record) => {
        acc.amountToRelease += Number(record.amountToRelease) || 0;
        acc.gst += Number(record.gst) || 0;
        acc.processingCharge += Number(record.processingCharge) || 0;
        return acc;
      },
      { amountToRelease: 0, gst: 0, processingCharge: 0 }
    );
  };

  const loanTotals = calculateLoanTotals();

  const validateForm = useCallback(() => {
    const total = parseFloat(totalCashCollection);
    if (!totalCashCollection || isNaN(total) || total < 0) {
      return { isValid: false, error: 'Please enter a valid total cash collection.' };
    }

    if (Math.abs(total - Number(outstandingCash)) > 0.01) {
      return {
        isValid: false,
        error: `Total cash collection (₹${total.toFixed(2)}) must equal outstanding cash (₹${Number(outstandingCash || 0).toFixed(2)}).`,
      };
    }

    const denominationsTotal = Object.entries(cashDenominations).reduce(
      (sum, [denomination, count]) => sum + parseInt(denomination) * (Number(count) || 0),
      0
    );
    const changeValue = parseFloat(change) || 0;
    const totalEntered = denominationsTotal + changeValue;

    // The denomination counts describe cash RECEIVED (gross deposits), while
    // `total` is the NET cash position after withdrawals. Comparing them was the
    // reason legitimate submissions were rejected on any day with a payout.
    if (Math.abs(totalEntered - grossCashDeposits) > 0.01) {
      return {
        isValid: false,
        error: `Denominations (₹${denominationsTotal.toFixed(2)}) + change (₹${changeValue.toFixed(2)}) must equal cash received today (₹${Number(grossCashDeposits).toFixed(2)}).`,
      };
    }

    if (changeValue < 0) {
      return { isValid: false, error: 'Change cannot be negative.' };
    }

    const outstandingValue = parseFloat(outstanding);
    if (outstanding && (isNaN(outstandingValue) || outstandingValue < 0)) {
      return { isValid: false, error: 'Please enter a valid outstanding amount.' };
    }

    const expensesValue = parseFloat(expenses);
    if (expenses && (isNaN(expensesValue) || expensesValue < 0)) {
      return { isValid: false, error: 'Please enter a valid expenses amount.' };
    }

    const branchPayoutValue = parseFloat(branchPayout);
    if (branchPayout && (isNaN(branchPayoutValue) || branchPayoutValue < 0)) {
      return { isValid: false, error: 'Please enter a valid branch payout amount.' };
    }

    if (branchPayoutValue > 0 && (!branches || branches.trim() === '')) {
      return { isValid: false, error: 'Please enter branch names for branch payout.' };
    }

    // Funding is checked BEFORE staff approval, deliberately. Approving a
    // collection finalises part of the day; there is no point letting that
    // happen over money whose source was never recorded.

    // Members' savings out on loan and not yet put back.
    if (memberBorrowings > 0) {
      // Name each pot, matching middleware.js. A repayment clears ONE account,
      // so "₹8,000 is out on loan" makes the operator record one payment that
      // overpays one pot and leaves the other open — and the day-end then
      // refuses again with a smaller figure that reads as a contradiction.
      const rupees = (n) =>
        `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
      const parts = [];
      if (memberBorrowingsByPot.cash > 0) {
        parts.push(`${rupees(memberBorrowingsByPot.cash)} from members' CASH savings`);
      }
      if (memberBorrowingsByPot.bank > 0) {
        parts.push(`${rupees(memberBorrowingsByPot.bank)} from members' BANK savings`);
      }
      // Borrowed before the channel split, so its pot was never recorded.
      if (memberBorrowingsByPot.pooled > 0) {
        parts.push(`${rupees(memberBorrowingsByPot.pooled)} from an earlier borrowing`);
      }

      return {
        isValid: false,
        error:
          (parts.length > 0
            ? `${parts.join(' and ')} is out on loan.`
            : `${rupees(memberBorrowings)} of members' savings is out on loan.`) +
          ' Record a "Repay Member Savings" payment under Payments before closing the day.' +
          (parts.length > 1 ? ' One payment per pot.' : ''),
      };
    }

    // Members' savings above Cash + Bank (liquidityGap) is NOT checked here: the
    // FPO re-lends deposits by design. It is shown as a warning banner instead,
    // and withdrawals are protected at payout (addDetails.js CHANNEL GUARD).

    // An agency was DISBURSED more than was ever recorded as provided, so a
    // funding receipt is missing. Not a guess: the operator named the agency at
    // disbursement, which is what put the loan on that agency's own account.
    // `gap` is measured against gross disbursement, so it names the capital to
    // receipt and does not shrink as borrowers repay.
    if (unreceiptedAgencies.length > 0) {
      const detail = unreceiptedAgencies
        .map((a) => `${a.agency} (₹${Number(a.gap).toLocaleString('en-IN', { minimumFractionDigits: 2 })})`)
        .join(', ');
      return {
        isValid: false,
        error:
          `Lending exceeds the funding recorded for ${detail}. Record it under Ledger → Receipts — ` +
          'type "Borrowings" for an agency drawdown, or "Share Capital" for owners\' money — ' +
          'before closing the day.',
      };
    }

    if (!allApproved && staffCollections.length > 0) {
      return { isValid: false, error: 'All staff collections must be approved before submitting day-end process.' };
    }

    return { isValid: true, error: null };
    // memberBorrowingsByPot is listed by its own fields rather than the object,
    // so a re-fetch returning identical figures does not rebuild the callback.
  }, [totalCashCollection, outstandingCash, grossCashDeposits, cashDenominations, change, outstanding, expenses, branchPayout, branches, allApproved, staffCollections, memberBorrowings, memberBorrowingsByPot.cash, memberBorrowingsByPot.bank, memberBorrowingsByPot.pooled, unreceiptedAgencies]);

  useEffect(() => {
    const { isValid, error } = validateForm();
    setFormError(error);
    setFormValid(isValid);
  }, [validateForm]);

  const handleSubmit = () => {
    // ALWAYS use the transactionDate that is currently being displayed/processed.
    // This guarantees the user's selected business date is strictly respected.
    const submissionDate = transactionDate || getTodayDate();

    if (submissionDate === getTwoDaysBeforeDate() && twoDaysBeforeDayEndStatus === 'submitted') {
      alert("Two days before's day-end process already submitted.");
      return;
    }
    if (submissionDate === getYesterdayDate() && yesterdayDayEndStatus === 'submitted') {
      alert("Yesterday's day-end process already submitted.");
      return;
    }
    if (submissionDate === getTodayDate() && dayEndStatus === 'submitted') {
      alert("Today's day-end process already submitted.");
      return;
    }

    const { isValid, error } = validateForm();
    if (!isValid) {
      setFormError(error);
      return;
    }

    setOpenModal(true);
  };

  const submitDayEnd = async () => {
    // Guard against the double-click that produced duplicate day-end rows.
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError(null);
    setSubmissionSuccess(false);

    const changeValue = parseFloat(change) || 0;
    const expensesValue = parseFloat(expenses) || 0;
    const branchPayoutValue = parseFloat(branchPayout) || 0;
    const branchesValue = branchPayoutValue > 0 ? branches : null;
    const submissionDate = transactionDate || getTodayDate();

    // The day's movement per channel. `total` is their sum — the whole day's
    // movement — because existing reports already read it.
    const cashTotalValue = Number(cashMovement) || 0;
    const onlineTotalValue = Number(onlineTotal) || 0;

    const data = {
      // The day's whole movement, not a closing position. The closing figure is
      // derived from the ledger on read, so storing it here would be a second copy
      // that cannot respond to an opening-balance correction.
      total: cashTotalValue + onlineTotalValue,
      cashTotal: cashTotalValue,
      onlineTotal: onlineTotalValue,
      denomination_500: Number(cashDenominations[500]) || 0,
      denomination_200: Number(cashDenominations[200]) || 0,
      denomination_100: Number(cashDenominations[100]) || 0,
      denomination_50: Number(cashDenominations[50]) || 0,
      denomination_20: Number(cashDenominations[20]) || 0,
      denomination_10: Number(cashDenominations[10]) || 0,
      change: changeValue,
      // `outstanding` is loan outstanding — what members still owe. The server
      // recomputes it from loanstatement and overrides whatever is sent, so this
      // value is a placeholder only. The cash carry-forward is derived from the
      // ledger and no longer stored here.
      outstanding: 0,
      expenses: expensesValue,
      branchpayout: branchPayoutValue,
      branches: branchesValue,
      branchId,
      depstatus: 'submitted',
      date: submissionDate,
    };

    try {
      await axios.post(`${API_BASE_URL}/api/addDetails`, {
        ...data,
        model: 'DayEndProcess',
        type: 'Add',
      }, {
        headers: { 'x-token': jwt }, // DayEndProcess is guarded by DayEndAdminOnly (admin/superadmin)
      });
      setSubmissionSuccess(true);
      if (submissionDate === getTodayDate()) {
        setShowDayEnd(false);
      }
      if (submissionDate === getTwoDaysBeforeDate()) {
        setTwoDaysBeforeDayEndStatus('submitted');
      } else if (submissionDate === getYesterdayDate()) {
        setYesterdayDayEndStatus('submitted');
      } else {
        setDayEndStatus('submitted');
      }
      await fetchDayEndStatus();

      // Refresh the entire app after successful day-end submission
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setFormError(err.response?.data?.error || err.response?.data?.message || 'Failed to submit day-end process.');
      setIsSubmitting(false);
    }
  };

  const handleConfirmSubmit = async () => {
    // Keep the modal open while the request is in flight so the disabled button
    // is what the user sees. Closing first would return them to the page with a
    // live Submit button and allow a second POST.
    if (isSubmitting) return;
    await submitDayEnd();
    setOpenModal(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCloseSnackbar = () => {
    setSubmissionSuccess(false);
  };

  const handleApprove = async (staff) => {
    try {
      const collectionData = {
        staffId: staff.id,
        name: staff.name,
        cashCollection: staff.cashCollection,
        onlineCollection: staff.onlineCollection,
        totalCollection: staff.totalCollection,
        date: staff.date,
        approvedStatus: true,
        approvedBy: data?.id || null,
        branchId: branchId,
        message: null,
      };

      try {
        await axios.post(`${API_BASE_URL}/api/addDetails`, {
          model: 'field_officer_collections',
          type: 'Add',
          ...collectionData,
        }, {
          headers: { 'x-token': jwt }, // field_officer_collections is guarded by DayEndAdminOnly (admin/superadmin)
        });
      } catch (err) {
        // ONLY a duplicate is ignorable — this staff member is already approved
        // for this date, which is the state the click was asking for. Reporting
        // it as a failure left the operator unable to clear the modal, since
        // re-approving can only ever 409 again.
        //
        // KEYED ON THE CODE, NOT THE STATUS. This used to swallow every 409, and
        // the funding blockers answer 409 too (middleware.js:292, :312, :330).
        // So a refused approval was reported to the operator as a green
        // "Approved" badge while the server saved nothing — visible only on the
        // next page load, when the row was unapproved again with no explanation.
        //
        // Not matched on the message text either: that breaks silently the day
        // someone rewords a sentence, and fails in the direction that blocks
        // legitimate re-approval.
        if (err.response?.data?.code !== 'DUPLICATE_RECORD') throw err;
      }

      // Built explicitly rather than read from state: setApprovedCollections below
      // does not take effect until the next render.
      const nextApproved = [
        ...approvedCollections.filter(
          (r) => !(r.staffId === staff.id && r.date === staff.date)
        ),
        collectionData,
      ];

      setApprovedCollections(nextApproved);
      setStaffRecords((prev) =>
        prev.map((s) =>
          s.id === staff.id
            ? { ...s, approvedStatus: true, approvedBy: data?.id || 'Admin' }
            : s
        )
      );

      // No optimistic `|| s.id === staff.id` override here. That short-circuit forced
      // the modal shut for whoever was just clicked, hiding the fact that the real
      // comparison never matched — and it is why repeated approvals accumulated
      // 3,065 duplicate rows before the backend guard existed.
      const staffCollections = calculateStaffCollections(nextApproved);
      const allCollectionsApproved = staffCollections.every((s) =>
        nextApproved.some((record) => record.staffId === s.id && record.date === s.date)
      );
      setAllApproved(allCollectionsApproved);
      setIsStaffCollectionsModalVisible(!allCollectionsApproved && staffCollections.length > 0);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to approve staff collection.';
      // BOTH: formError for the Close-the-day tab, and the snackbar because the
      // Approve button lives in the STAFF tab, where formError is not rendered.
      // Without the snackbar a refused approval failed silently on the very
      // screen the operator was looking at.
      setFormError(msg);
      showError(msg);
    }
  };

  const isFormDisabled =
    (twoDaysBeforeDayEndStatus === 'submitted' && transactionDate === getTwoDaysBeforeDate()) ||
    (yesterdayDayEndStatus === 'submitted' && transactionDate === getYesterdayDate()) ||
    (dayEndStatus === 'submitted' && transactionDate === getTodayDate());

  // Money has gone out with no recorded source. Gates the staff-approval popup
  // as well as the form, so the two prompts appear in the order the work has to
  // happen: record the funding first, then approve the collections.
  const fundingBlocked =
    unreceiptedAgencies.length > 0 || memberBorrowings > 0;


  // ── Render helpers ──────────────────────────────────────────────────────
  const inr = (n) =>
    Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // The bank's closing position. Computed ONCE because the panel below shows it
  // as the headline AND tests it for an overdraft note — two copies of the same
  // expression could drift and report a figure the note contradicts.
  const bankClosingValue = bankMovementLedger
    ? Number(bankOpening) + Number(bankMovementLedger.net)
    : Number(bankOpening) + Number(onlineTotal);

  // The date being closed, named.
  //
  // These used to read "two days before" / "yesterday" / "today", computed
  // against the current date. During a catch-up that describes the GAP rather
  // than the day — closing 9/5 on a Monday showed "Submit for Two Days Before",
  // which reads as though it submits a range. Worse, the final `else` meant any
  // date older than two days — now reachable, since the strip works through the
  // whole backlog — was labelled "Today" while closing a day from last week.
  //
  // Naming the date removes both. "Today" is kept only when it really is today.
  const isClosingToday = transactionDate === getTodayDate();
  const dayLabel = isClosingToday ? 'today' : formatDate(transactionDate);
  const dayLabelTitle = isClosingToday ? 'Today' : formatDate(transactionDate);

  // Business date as a real control, built from the server's open-days list.
  //
  // It used to be a fixed [two-days-before, yesterday, today] guess, which showed
  // 9/4, 9/5, 9/7 on a Monday: the Sunday was skipped by the old date helper even
  // though it carried 9 transactions worth Rs.10,05,000, and an older backlog was
  // unreachable entirely.
  //
  // Today is appended when nothing is outstanding, so the strip is never empty.
  // Days must be closed OLDEST FIRST — each day's outstanding is the next day's
  // opening balance — so only the oldest open day is selectable.
  const dateOptions = (
    pendingDates.length > 0 ? pendingDates : [getTodayDate()]
  ).map((date, index) => ({
    date,
    status: date === getTodayDate() ? dayEndStatus : 'pending',
    // The oldest open day, and today once the backlog is clear.
    selectable: index === 0,
    isSunday: isSunday(date),
  }));

  const denomList = [500, 200, 100, 50, 20, 10];

  const countedTotal =
    denomList.reduce((s, d) => s + d * (Number(cashDenominations[d]) || 0), 0)
    + (parseFloat(change) || 0);

  const pendingStaffCount = staffCollections.filter((s) => !s.approvedStatus).length;

  const fieldCls =
    'px-3 py-2 text-sm tabular-nums rounded-lg border border-border-light bg-white ' +
    'text-ink-900 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary ' +
    'disabled:bg-canvas-base disabled:text-ink-400';

  // A money field. Deliberately plain: these are the figures stored on the
  // day-end row, and decoration makes them harder to scan than to type.
  const renderField = ({ id, label, value, onChange, hint, type = 'number', disabled }) => (
    <div className="flex flex-col gap-1.5" key={id}>
      <label htmlFor={id} className="text-[11px] font-semibold text-ink-400 tracking-wide">{label}</label>
      <input
        id={id}
        type={type}
        min="0"
        step={type === 'number' ? '0.01' : undefined}
        value={value}
        onChange={onChange}
        disabled={disabled ?? isFormDisabled}
        className={fieldCls}
      />
      {hint && <span className="text-[11px] text-ink-400">{hint}</span>}
    </div>
  );

  // Cash and Online are the two things being closed, so they get identical
  // weight. The role caption is load-bearing: it is what stops an operator
  // counting notes against a figure that contains UPI money.
  const renderChannel = ({ tone, title, role, rows, headline, headlineValue, note, noteWarn }) => {
    const t = tone === 'cash'
      ? { head: 'bg-teal-50', text: 'text-teal-700' }
      : { head: 'bg-sky-50', text: 'text-sky-700' };
    return (
      <div className="bg-white border border-border-light rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className={`px-3 py-1 flex items-center gap-2 border-b border-border-light ${t.head}`}>
          <span className={`text-[11px] font-black uppercase tracking-[0.09em] ${t.text}`}>{title}</span>
          <span className="ml-auto text-[10px] text-ink-400">{role}</span>
        </div>
        <div className="px-3 py-1.5 flex flex-col gap-1">
          {/* Supporting figures on ONE inline strip rather than stacked rows: the
              headline is what gets acted on, and the inputs to it read better as a
              single line than as a list competing with it for attention. */}
          <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap text-[11px] text-ink-500">
            {rows.filter((r) => !r.blank).map((r, i) => (
              <span key={i} className="whitespace-nowrap">
                {r.label} <span className="tabular-nums text-ink-900 font-medium">₹{inr(r.value)}</span>
              </span>
            ))}
          </div>
          <div className="h-px bg-border-light" />
          <div className="flex items-baseline justify-between gap-2" title={!noteWarn ? note : undefined}>
            <span className="text-[10.5px] font-black uppercase tracking-[0.07em] text-ink-500">{headline}</span>
            <span className={`text-[18px] leading-none font-black tabular-nums tracking-tight ${t.text}`}>
              ₹{inr(headlineValue)}
            </span>
          </div>
          {/* A plain footnote becomes a tooltip; a warning stays visible, since it
              reports something unconfigured rather than merely explanatory. */}
          {note && noteWarn && (
            <p className="text-[10px] text-warning-700 leading-tight">{note}</p>
          )}
        </div>
      </div>
    );
  };

  const renderTab = (id, label, count, alert) => (
    <button
      type="button"
      role="tab"
      aria-selected={activeTab === id}
      onClick={() => setActiveTab(id)}
      className={[
        'px-4 py-2.5 text-[13px] font-semibold whitespace-nowrap inline-flex items-center gap-2',
        '-mb-px border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        activeTab === id ? 'text-primary border-primary'
          : 'text-ink-400 border-transparent hover:text-ink-600',
      ].join(' ')}
    >
      {label}
      {typeof count === 'number' && (
        <span className={[
          'text-[11px] font-semibold rounded-full px-1.5 min-w-[20px] text-center',
          alert ? 'bg-warning-500 text-white'
            : activeTab === id ? 'bg-primary text-white'
              : count === 0 ? 'bg-border-light text-ink-400 opacity-60'
                : 'bg-border-light text-ink-600',
        ].join(' ')}>{count}</span>
      )}
    </button>
  );

  const renderSection = (loading, error, children) => {
    if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (error) return <div className="py-6 text-center text-sm text-danger-600">{error}</div>;
    return children;
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <PageLayout
      badge={
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Compact Closing for Selector */}
          <div className="inline-flex items-center gap-1.5 bg-canvas-base border border-border-light rounded-lg px-2 py-0.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-ink-400 select-none">Closing for:</span>
            <div className="inline-flex items-center gap-1">
              {dateOptions.map((d) => (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => d.selectable && setTransactionDate(d.date)}
                  disabled={!d.selectable}
                  aria-pressed={transactionDate === d.date}
                  title={
                    d.selectable
                      ? undefined
                      : 'Days must be closed oldest first — each day’s closing balance opens the next.'
                  }
                  className={[
                    'px-2 py-0.5 text-[11.5px] rounded inline-flex items-center gap-1.5 transition-colors',
                    'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40',
                    transactionDate === d.date
                      ? 'bg-white text-ink-900 font-bold shadow-xs'
                      : d.selectable
                        ? 'text-ink-500 hover:text-ink-900'
                        : 'text-ink-300 cursor-not-allowed',
                  ].join(' ')}
                >
                  {formatDate(d.date)}
                  {d.isSunday && (
                    <span className="text-[9px] font-semibold text-ink-400">Sun</span>
                  )}
                  <span className={[
                    'text-[9px] font-bold uppercase tracking-wide px-1.5 py-px rounded',
                    d.status === 'submitted' ? 'bg-success-50 text-success-700'
                      : 'bg-warning-50 text-warning-700',
                  ].join(' ')}>
                    {d.status === 'submitted' ? 'Closed' : 'Open'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <Badge variant={isFormDisabled ? 'success' : 'warning'}>
            {isFormDisabled ? 'Submitted' : 'Pending'}
          </Badge>
        </div>
      }
      actions={
        <div className="flex items-center gap-3 print:hidden">
          {isSuperAdmin && (
            <select
              value={selectedBranch}
              onChange={(e) => { setSelectedBranch(e.target.value); fetchDayEndStatus(); }}
              className="px-2.5 py-1 h-[30px] text-xs rounded-md border border-border-light bg-white text-ink-900
                         focus:outline-none focus:ring-2 focus:ring-primary/40 min-w-[160px]"
            >
              <option value="">Select branch</option>
              {allBranches.map((b) => (
                <option key={b.id} value={b.id}>{b.branchName}</option>
              ))}
            </select>
          )}
          <Button
            variant="primary"
            size="sm"
            className="h-[30px] px-3 text-xs"
            onClick={handleSubmit}
            disabled={!formValid || isFormDisabled || isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : `Submit for ${dayLabelTitle}`}
          </Button>
        </div>
      }
    >
      {/* Portals to document.body, so its position here does not matter — but it
          must be rendered somewhere or no message appears. */}
      <SnackbarHost />
      {/* THE page's single scroll region. PageLayout fixes the outer height and
          hides its own overflow, so exactly one element below it may scroll.
          Everything inside takes its natural height — no child claims `flex-1`,
          which is what previously collapsed the tab panel toward zero and left
          the table with no height and no scrollbar reaching it. */}
      <div className="flex flex-col gap-3 min-h-0 flex-1 overflow-y-auto pb-4 pr-0.5">

        {/* Liquidity warning — NEVER blocks the close. Members may withdraw their
            savings at any time and the FPO re-lends them, so savings above Cash +
            Bank is normal; this shows how exposed the branch is. Withdrawals that
            the drawer cannot cover are refused at payout. */}
        {savingsShortfall > 0 && (
          <div className="shrink-0 rounded-lg border border-border-light bg-warning-50 px-4 py-3 text-sm text-warning-700">
            <div className="font-semibold">Liquidity warning — not a blocker</div>
            <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1">
              <span>Member savings: <strong>₹{inr(liquidity.savings)}</strong></span>
              <span>Cash + Bank: <strong>₹{inr(liquidity.liquid)}</strong></span>
              <span>Gap (withdrawal risk): <strong>₹{inr(savingsShortfall)}</strong></span>
            </div>
            <div className="mt-1 text-xs">
              If members ask for more than the cash and bank in hand, those withdrawals will be refused
              until EMIs, deposits or a receipt bring money in.
            </div>
          </div>
        )}

        {/* Two channels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 shrink-0">
          {renderChannel({
            tone: 'cash',
            title: 'Cash',
            role: 'counted tonight',
            // From the LEDGER where the branch has one — it sees the cash-to-bank
            // transfer, which carries no cash_type and was therefore missing from
            // the transactions-derived figures. Falls back on older dates.
            rows: cashMovementLedger
              ? [
                { label: 'Opening cash', value: yesterdayOutstanding },
                { label: 'Cash in', value: cashMovementLedger.moneyIn },
                { label: 'Cash out', value: cashMovementLedger.moneyOut },
              ]
              : [
                { label: 'Opening cash', value: yesterdayOutstanding },
                { label: 'Deposits', value: totalDepositAmount },
                { label: 'Withdrawals', value: totalWithdrawalAmount },
              ],
            headline: 'Expected in safe',
            headlineValue: cashMovementLedger
              ? Number(yesterdayOutstanding) + Number(cashMovementLedger.net)
              : outstandingCash,
            note: "Carried forward as tomorrow's opening cash.",
          })}
          {renderChannel({
            tone: 'bank',
            title: 'Online / Bank',
            role: 'statement-verified',
            // From the LEDGER where available. This is what catches the loan
            // proceeds a member draws — cash_type NULL, so the old figure missed
            // it entirely and reported a healthy balance while the bank was
            // overdrawn. It also covers EVERY bank account, not just the one
            // resolved by account_code.
            rows: bankMovementLedger
              ? [
                { label: 'Opening bank', value: bankOpening },
                { label: 'Bank in', value: bankMovementLedger.moneyIn },
                { label: 'Bank out', value: bankMovementLedger.moneyOut },
              ]
              : [
                { label: 'Opening bank', value: bankOpening },
                { label: 'Online movement', value: onlineTotal },
                { label: ' ', blank: true },
              ],
            headline: 'Expected balance',
            headlineValue: bankClosingValue,
            // An OVERDRAWN bank is said out loud, and it wins over the other
            // notes. It is a legitimate position — a loan disbursed before its
            // funding receipt is recorded puts the bank here by design — but it
            // must never be silent: the savingsShortfall blocker is scoped to
            // branches that have borrowed members' savings (fundsAvailable.js:168),
            // so a branch that never has can sit overdrawn with nothing
            // reporting it. Branch 1 closed days at -Rs.80,000 unremarked.
            note: bankClosingValue < 0
              ? `Overdrawn by ₹${inr(Math.abs(bankClosingValue))} — the bank has paid out more than it holds. ` +
              "Record the funding receipt under Ledger > Receipts (Borrowings for an agency drawdown, Share Capital for owners' money)."
              : bankMovementLedger && bankMovementLedger.accounts.length > 1
                ? `Across ${bankMovementLedger.accounts.length} bank accounts — verified against the statements.`
                : Number(bankOpening) === 0
                  ? 'No bank opening balance entered for this branch — verified against the statement, not counted here.'
                  : 'Verified against the bank statement — not counted here.',
            noteWarn: bankClosingValue < 0 || Number(bankOpening) === 0,
          })}
        </div>

        {/* Tabs. Natural height — the page scrolls, not this panel, so a long
            table extends the card rather than being clipped inside it. The tab
            strip stays stuck to the top of the card while its rows scroll past. */}
        <div
          ref={tabCardRef}
          className="bg-white border border-border-light rounded-xl shadow-sm
                     flex flex-col shrink-0"
        >
          {/* z-30, not z-10: the table headings inside every tab are ALSO
              `sticky top-0`, on this same page scroller, because each <Table>
              here is rendered flex-none/max-h-none and so does not scroll inside
              its own box. Two elements claiming one strip of screen at the same
              z-index is what printed the tab names across the rows.
              The strip must win, and the headings park beneath it — see
              --table-head-offset below.

              bg-[#F8FAFC] replaces bg-canvas-base, which is not defined in
              tailwind.config.js and therefore painted NOTHING: rows showed
              straight through the strip as it passed over them. Same literal the
              table heading uses, so the two read as one surface. */}
          <div
            ref={tabStripRef}
            role="tablist"
            className="flex gap-0.5 px-2 border-b border-border-light
                       bg-[#F8FAFC] overflow-x-auto rounded-t-xl
                       sticky top-0 z-30"
          >
            {renderTab('transactions', 'Transactions', filteredCashRecords.length)}
            {renderTab('close', 'Close the day')}
            {renderTab('staff', 'Staff', staffCollections.length, pendingStaffCount > 0)}
            {renderTab('loans', 'Loans', loanRecords.length)}
            {renderTab('ledger', 'Ledger', filteredLedgerRecords.length)}
          </div>

          <div className="px-4 py-4 rounded-b-xl">

            {/* ---------- TRANSACTIONS ---------- */}
            {activeTab === 'transactions' && renderSection(loadingCash, errorCash, (
              <>
                <div className="flex items-center gap-4 flex-wrap mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-400">Type</span>
                    <SegmentedControl
                      value={filterType}
                      onChange={setFilterType}
                      options={[
                        { value: 'All', label: 'All' },
                        { value: 'Deposit', label: 'Deposit' },
                        { value: 'Withdraw', label: 'Withdraw' },
                      ]}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-400">Channel</span>
                    <SegmentedControl
                      value={transactionFilter}
                      onChange={setTransactionFilter}
                      options={[
                        { value: 'All', label: 'All' },
                        { value: 'Cash', label: 'Cash' },
                        { value: 'Online', label: 'Online' },
                      ]}
                    />
                  </div>

                  {/* How many rows exist, and how many the filters are hiding.
                      This table is deliberately flex-none/max-h-none so the PAGE
                      scrolls rather than the table (see the z-30 note above), so
                      a long day simply runs past the fold with nothing saying
                      so. An operator comparing the approval popup's Rs.16,000
                      against two visible rows reads that as missing money — it
                      cost a full investigation to prove the third row was there
                      all along, below the viewport. */}
                  <div className="ml-auto text-[12px] text-ink-500 tabular-nums">
                    Showing <span className="font-semibold text-ink-700">{filteredCashRecords.length}</span>
                    {' of '}
                    <span className="font-semibold text-ink-700">{cashRecords.length}</span>
                    {' '}transaction{cashRecords.length === 1 ? '' : 's'}
                    {filteredCashRecords.length < cashRecords.length && (
                      <span className="text-warning-700">
                        {' '}&middot; {cashRecords.length - filteredCashRecords.length} hidden by filters
                      </span>
                    )}
                  </div>
                </div>

                {/* overflow-visible is REQUIRED on every table on this page, not
                    cosmetic. Table's root carries `overflow-auto`
                    (Table.jsx:47), and an overflow-auto element is its OWN
                    scroll container — so the sticky thead resolves
                    `top: var(--table-head-offset)` against the TABLE's box
                    instead of the page, parking the header ~50px down from the
                    table's own top, directly over the FIRST data row.
                    That is what hid transaction 226 (Rs.5,000) and the filter
                    bar above it: both were rendered, then covered. The API
                    returned 10 rows with 226 first; the counter read
                    "Deposits (4)" against 3 visible rows, which is the
                    signature of a covered row rather than a missing one.
                    With overflow-visible the table stops being a scroller, the
                    page scroller takes over, and the offset means what it was
                    written to mean — park beneath the tab strip. */}
                <Table className="flex-none max-h-none overflow-visible">
                  <Table.Head>
                    <tr>
                      <Table.HeadCell align="left">Type</Table.HeadCell>
                      <Table.HeadCell align="left">Payment</Table.HeadCell>
                      <Table.HeadCell align="right">Total (₹)</Table.HeadCell>
                      {denomList.map((d) => (
                        <Table.HeadCell key={d} align="right">₹{d}</Table.HeadCell>
                      ))}
                      <Table.HeadCell align="right">Change (₹)</Table.HeadCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {filteredCashRecords.length === 0 ? (
                      <Table.EmptyRow colSpan={10} message={`No cash records for ${dayLabel}`} />
                    ) : (
                      filteredCashRecords.map((record) => (
                        <Table.Row key={record.id || Math.random()}>
                          <Table.Cell align="left">{getTransactionType(record)}</Table.Cell>
                          <Table.Cell align="left">
                            {/* A NULL cash_type means the money never took a
                                channel — the loan disbursement and its deductions
                                only changed the member's balance while the FPO
                                went on holding the money. Shown as "online"
                                because that is how the branch reads a non-cash
                                movement, and "N/A" looked like missing data.
                                DISPLAY ONLY — the stored value stays NULL, which
                                is what tells the member-savings cap in
                                ledgerPosting.js which rows are real member money.
                                On live 2,182 rows netting Rs.8.99 crore depend on
                                that distinction. */}
                            <span className={[
                              'text-[11px] font-semibold px-2 py-0.5 rounded',
                              record.cash_type === 'cash' ? 'bg-teal-50 text-teal-700' : 'bg-sky-50 text-sky-700',
                            ].join(' ')}>
                              {record.cash_type === 'cash' ? 'cash' : 'online'}
                            </span>
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">{inr(calculateTotal(record))}</Table.Cell>
                          {denomList.map((d) => (
                            <Table.Cell key={d} align="right" className="tabular-nums">
                              {Number(record[String(d)]) || 0}
                            </Table.Cell>
                          ))}
                          <Table.Cell align="right" className="tabular-nums">{inr(record.change)}</Table.Cell>
                        </Table.Row>
                      ))
                    )}
                    {/* Totals sit in the table, under the Total column they
                        describe, rather than in a separate box below it. Deposits
                        and withdrawals are kept apart: adding them produced
                        Rs.10,05,000 for a day of Rs.5,05,000 in and Rs.5,00,000
                        out, which is not a quantity of anything. */}
                    {filteredCashRecords.length > 0 && (
                      <>
                        {/* The COUNT beside the money, so the totals row can be
                            checked against the approval popup without scrolling
                            the table to count rows by eye. */}
                        <Table.Row className="bg-[#F8FAFC] font-semibold">
                          <Table.Cell align="left" colSpan={2}>
                            Deposits
                            <span className="ml-1.5 font-normal text-ink-500">
                              ({depositRowCount})
                            </span>
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums text-success-700">
                            +{inr(cashTotals.depositTotal)}
                          </Table.Cell>
                          <Table.Cell colSpan={7} />
                        </Table.Row>
                        <Table.Row className="bg-[#F8FAFC] font-semibold">
                          <Table.Cell align="left" colSpan={2}>
                            Withdrawals
                            <span className="ml-1.5 font-normal text-ink-500">
                              ({withdrawRowCount})
                            </span>
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums text-danger-700">
                            &minus;{inr(cashTotals.withdrawTotal)}
                          </Table.Cell>
                          <Table.Cell colSpan={7} />
                        </Table.Row>
                        <Table.Row className="bg-canvas-base font-bold border-t-2 border-border-light">
                          <Table.Cell align="left" colSpan={2}>
                            Net {transactionFilter === 'All' && filterType === 'All' ? '' : '(filtered)'}
                            {/* Named because this net and the Ledger tab's differ
                                legitimately, and without saying what each counts
                                they read as a contradiction. A disbursement is a
                                deposit to the member's account with no cash
                                moving; the deductions come back out of the same
                                balance. Only what the member actually draws
                                reaches the bank. */}
                            <span className="ml-2 font-normal text-ink-300 text-[12px]">
                              on members&rsquo; accounts
                            </span>
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">
                            {cashTotals.depositTotal - cashTotals.withdrawTotal >= 0 ? '+' : '−'}
                            {inr(Math.abs(cashTotals.depositTotal - cashTotals.withdrawTotal))}
                          </Table.Cell>
                          <Table.Cell colSpan={7} />
                        </Table.Row>
                      </>
                    )}
                  </Table.Body>
                </Table>

                {/* Cash Total and Online Total always describe the whole day —
                    reading them from the filtered rows made Online Total show
                    0.00 whenever CASH was selected, which reads as "there was no
                    online money today". */}
                <div className="mt-2 border border-[#CBD5E1] rounded-lg overflow-hidden grid grid-cols-1 sm:grid-cols-2 bg-white">
                  <div className="flex items-center justify-between px-4 py-2 border-b sm:border-b-0 sm:border-r border-[#CBD5E1]">
                    <span className="text-[13px] text-ink-500 font-medium">Cash total (whole day)</span>
                    <span className="text-[13px] font-semibold tabular-nums text-ink-900">₹{inr(dayTotals.cashTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2">
                    <span className="text-[13px] text-ink-500 font-medium">Online total (whole day)</span>
                    <span className="text-[13px] font-semibold tabular-nums text-ink-900">₹{inr(dayTotals.onlineTotal)}</span>
                  </div>
                </div>
              </>
            ))}

            {/* ---------- CLOSE THE DAY ---------- */}
            {activeTab === 'close' && renderSection(loadingDayEndStatus, null, (
              isFormDisabled ? (
                <>
                  <div className="px-4 py-3 rounded-lg bg-success-50 text-success-700 text-sm mb-4">
                    Day-end process submitted for {dayLabel}.
                  </div>
                  {todaysDayEndRecord && transactionDate === getTodayDate() && (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                        {renderField({ id: 'v-total', label: 'Total Cash (₹)', value: totalCashCollection, disabled: true })}
                        {denomList.map((d) => renderField({
                          id: `v-${d}`, label: `₹${d}`, value: Number(cashDenominations[d]) || 0, disabled: true,
                        }))}
                        {renderField({ id: 'v-change', label: 'Change (₹)', value: change, disabled: true })}
                        {renderField({ id: 'v-out', label: 'Outstanding (₹)', value: outstanding, disabled: true })}
                        {renderField({ id: 'v-exp', label: 'Expenses (₹)', value: expenses, disabled: true })}
                        {renderField({ id: 'v-pay', label: 'Branch Payout (₹)', value: branchPayout, disabled: true })}
                        {branchPayout && Number(branchPayout) > 0 &&
                          renderField({ id: 'v-br', label: 'Branches', value: branches, type: 'text', disabled: true })}
                      </div>
                      <p className="text-[11px] text-ink-400 mt-3">
                        Submitted on: {formatDate(todaysDayEndRecord.createdAt || todaysDayEndRecord.date)}
                      </p>
                    </>
                  )}
                </>
              ) : outstandingCash === 0 && cashRecords.length === 0 ? (
                <div className="px-4 py-3 rounded-lg bg-primary-50 text-ink-600 text-sm">
                  No cash transactions for {dayLabel}.
                </div>
              ) : (
                <div className="flex flex-col gap-6">

                  <section>
                    <h3 className="text-[12px] font-black uppercase tracking-[0.06em] text-ink-400 mb-1">
                      1 · Count the cash received
                    </h3>
                    <p className="text-[12.5px] text-ink-400 mb-3">
                      Notes and change must equal <strong className="text-ink-600">cash received today</strong>{' '}
                      (gross deposits, ₹{inr(grossCashDeposits)}) — not the expected balance. Withdrawals do
                      not reduce what you counted in.
                    </p>
                    {/* Empty boxes on a day with no cash read as a form waiting to
                        be filled. Said plainly instead, so the operator knows there
                        is nothing to do here and submits. */}
                    {Number(grossCashDeposits) === 0 && (
                      <p className="text-[12.5px] text-success-700 mb-3">
                        No cash was received today — nothing to count. Leave these empty.
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                      {denomList.map((d) => (
                        <div className="flex flex-col gap-1.5" key={d}>
                          <label htmlFor={`d-${d}`} className="text-[11px] font-semibold text-ink-400">₹{d}</label>
                          <input
                            id={`d-${d}`}
                            type="number"
                            min="0"
                            step="1"
                            value={cashDenominations[d] || ''}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              setCashDenominations((prev) => ({ ...prev, [d]: isNaN(v) ? 0 : v }));
                            }}
                            disabled={isFormDisabled}
                            className={fieldCls}
                          />
                          <span className="text-[11px] text-ink-400 tabular-nums">
                            = ₹{((Number(cashDenominations[d]) || 0) * d).toLocaleString('en-IN')}
                          </span>
                        </div>
                      ))}
                      {renderField({
                        id: 'f-change', label: 'Change (₹)', value: change,
                        onChange: (e) => setChange(e.target.value), hint: 'coins & loose',
                      })}
                    </div>

                    <div className="mt-3 px-4 py-3 rounded-lg bg-canvas-base border border-border-light
                                    flex items-center gap-4 flex-wrap">
                      <div className="flex flex-col">
                        <span className="text-[11px] text-ink-400">Counted</span>
                        <b className="text-[17px] font-black tabular-nums text-ink-900">₹{inr(countedTotal)}</b>
                      </div>
                      <span className="text-ink-400 text-[15px] pt-3">must equal</span>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-ink-400">Cash received today</span>
                        <b className="text-[17px] font-black tabular-nums text-ink-900">₹{inr(grossCashDeposits)}</b>
                      </div>
                      <span className={[
                        'ml-auto text-[12.5px] font-semibold',
                        Math.abs(countedTotal - Number(grossCashDeposits)) < 0.01
                          ? 'text-success-700' : 'text-warning-700',
                      ].join(' ')}>
                        {Math.abs(countedTotal - Number(grossCashDeposits)) < 0.01
                          ? 'Matches'
                          : countedTotal > Number(grossCashDeposits)
                            ? `Over by ₹${inr(countedTotal - Number(grossCashDeposits))}`
                            : `Short by ₹${inr(Number(grossCashDeposits) - countedTotal)}`}
                      </span>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-[12px] font-black uppercase tracking-[0.06em] text-ink-400 mb-1">
                      2 · Day figures
                    </h3>
                    <p className="text-[12.5px] text-ink-400 mb-3">Totals recorded on the day-end row.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {renderField({
                        id: 'f-total', label: 'Total Cash (₹)', value: totalCashCollection,
                        onChange: (e) => setTotalCashCollection(e.target.value), hint: 'Cash collected today',
                      })}
                      {renderField({
                        id: 'f-out', label: 'Outstanding (₹)', value: outstanding,
                        onChange: (e) => setOutstanding(e.target.value), hint: 'Server recomputes this on submit',
                      })}
                      {renderField({
                        id: 'f-exp', label: 'Expenses (₹)', value: expenses,
                        onChange: (e) => setExpenses(e.target.value), hint: 'Paid out of the till today',
                      })}
                    </div>
                  </section>

                  <section>
                    <h3 className="text-[12px] font-black uppercase tracking-[0.06em] text-ink-400 mb-1">
                      3 · Branch payout
                    </h3>
                    <p className="text-[12.5px] text-ink-400 mb-3">
                      Leave at zero if no cash was sent to another branch.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {renderField({
                        id: 'f-pay', label: 'Branch Payout (₹)', value: branchPayout,
                        onChange: (e) => setBranchPayout(e.target.value), hint: 'Cash sent elsewhere',
                      })}
                      {/* Shown only when a payout exists — mirrors the validation
                          rule requiring a branch name above zero. */}
                      {branchPayout && Number(branchPayout) > 0 && renderField({
                        id: 'f-br', label: 'Branch Name', value: branches, type: 'text',
                        onChange: (e) => setBranches(e.target.value),
                        hint: 'Required when payout is above zero',
                      })}
                    </div>
                  </section>

                  {formError && (
                    <div className="px-4 py-3 rounded-lg bg-danger-50 text-danger-700 text-sm">{formError}</div>
                  )}
                  {formValid && !isFormDisabled && (
                    <div className="px-4 py-3 rounded-lg bg-success-50 text-success-700 text-sm">
                      Ready to submit for {dayLabel}.
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={!formValid || isFormDisabled || isSubmitting}
                    >
                      {isSubmitting ? 'Submitting…' : `Submit for ${dayLabelTitle}`}
                    </Button>
                  </div>
                </div>
              )
            ))}

            {/* ---------- STAFF ---------- */}
            {activeTab === 'staff' && renderSection(loadingStaff, errorStaff, (
              <>
                <p className="text-[12.5px] text-ink-400 mb-3">
                  Every staff collection must be approved before the day can close.
                </p>
                <Table className="flex-none max-h-none overflow-visible">
                  <Table.Head>
                    <tr>
                      <Table.HeadCell align="left">Staff ID</Table.HeadCell>
                      <Table.HeadCell align="left">Name</Table.HeadCell>
                      <Table.HeadCell align="right">Cash Collection (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Online Collection (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Total Collection (₹)</Table.HeadCell>
                      <Table.HeadCell align="left">Date</Table.HeadCell>
                      <Table.HeadCell align="center">Action</Table.HeadCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {staffCollections.length === 0 ? (
                      <Table.EmptyRow colSpan={7} message="No staff collections data available." />
                    ) : (
                      <>
                        {staffCollections.map((staff) => (
                          <Table.Row key={staff.id}>
                            <Table.Cell align="left">{staff.id}</Table.Cell>
                            <Table.Cell align="left">{staff.name}</Table.Cell>
                            <Table.Cell align="right" className="tabular-nums">{inr(staff.cashCollection)}</Table.Cell>
                            <Table.Cell align="right" className="tabular-nums">{inr(staff.onlineCollection)}</Table.Cell>
                            <Table.Cell align="right" className="tabular-nums">{inr(staff.totalCollection)}</Table.Cell>
                            <Table.Cell align="left">{staff.date || 'N/A'}</Table.Cell>
                            <Table.Cell align="center">
                              {/* The Approve button below carries
                                  disabled={fundingBlocked}, the same guard the
                                  modal's copy has. This one was missed when the
                                  funding rule was added, so a live Approve
                                  button sat on the Staff tab while the day was
                                  blocked for funding. */}
                              {staff.approvedStatus ? (
                                <Badge variant="success">Approved</Badge>
                              ) : (
                                <Button size="sm" variant="primary" disabled={fundingBlocked} onClick={() => handleApprove(staff)}>
                                  Approve
                                </Button>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                        <Table.Row className="bg-canvas-base font-semibold">
                          <Table.Cell align="left" colSpan={2}>Total</Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">
                            {inr(staffCollections.reduce((s, x) => s + x.cashCollection, 0))}
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">
                            {inr(staffCollections.reduce((s, x) => s + x.onlineCollection, 0))}
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">
                            {inr(staffCollections.reduce((s, x) => s + x.totalCollection, 0))}
                          </Table.Cell>
                          <Table.Cell colSpan={2} />
                        </Table.Row>
                      </>
                    )}
                  </Table.Body>
                </Table>
              </>
            ))}

            {/* ---------- LOANS ---------- */}
            {activeTab === 'loans' && renderSection(loadingLoan, errorLoan, (
              <>
                <p className="text-[12.5px] text-ink-400 mb-3">
                  Loans approved and awaiting disbursement on this date.
                </p>
                <Table className="flex-none max-h-none overflow-visible">
                  <Table.Head>
                    <tr>
                      <Table.HeadCell align="left">Applicant ID</Table.HeadCell>
                      <Table.HeadCell align="left">Date</Table.HeadCell>
                      <Table.HeadCell align="right">Amount (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">GST (₹)</Table.HeadCell>
                      <Table.HeadCell align="right">Fee (₹)</Table.HeadCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {loanRecords.length === 0 ? (
                      <Table.EmptyRow colSpan={5} message="No pending loan records" />
                    ) : (
                      <>
                        {loanRecords.map((record) => (
                          <Table.Row key={record.id || Math.random()}>
                            <Table.Cell align="left">{record.ApplicationId || 'N/A'}</Table.Cell>
                            <Table.Cell align="left">{formatDate(record.adminStatusUpdatedAt)}</Table.Cell>
                            <Table.Cell align="right" className="tabular-nums">{inr(record.amountToRelease)}</Table.Cell>
                            <Table.Cell align="right" className="tabular-nums">{inr(record.gst)}</Table.Cell>
                            <Table.Cell align="right" className="tabular-nums">{inr(record.processingCharge)}</Table.Cell>
                          </Table.Row>
                        ))}
                        <Table.Row className="bg-canvas-base font-semibold">
                          <Table.Cell align="left">Total</Table.Cell>
                          <Table.Cell align="left">–</Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">{inr(loanTotals.amountToRelease)}</Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">{inr(loanTotals.gst)}</Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">{inr(loanTotals.processingCharge)}</Table.Cell>
                        </Table.Row>
                      </>
                    )}
                  </Table.Body>
                </Table>
              </>
            ))}

            {/* ---------- LEDGER ---------- */}
            {activeTab === 'ledger' && renderSection(loadingLedger, errorLedger, (
              <>
                <div className="flex items-center gap-4 flex-wrap mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-400">Type</span>
                    {/* Filters on the DERIVED type (Deposit, Fee, Capital…), not
                        the raw receipt/payment, which no longer appears in the
                        table. Built from the day's own lines so it never offers
                        an option that matches nothing. */}
                    <select
                      value={ledgerTypeFilter}
                      onChange={(e) => setLedgerTypeFilter(e.target.value)}
                      className="px-3 py-2 text-[13px] rounded-lg border border-border-light bg-white
                                 text-ink-900 focus:outline-none focus:ring-2 focus:ring-primary/40 min-w-[150px]"
                    >
                      {ledgerTypeOptions.map((t) => (
                        <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-400">Account</span>
                    <select
                      value={ledgerAccountFilter}
                      onChange={(e) => setLedgerAccountFilter(e.target.value)}
                      className="px-3 py-2 text-[13px] rounded-lg border border-border-light bg-white
                                 text-ink-900 focus:outline-none focus:ring-2 focus:ring-primary/40 min-w-[200px]"
                    >
                      <option value="All">All Accounts</option>
                      {ledgerAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.account_type || 'N/A'})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <Table className="flex-none max-h-none overflow-visible">
                  <Table.Head>
                    <tr>
                      <Table.HeadCell align="left">ID</Table.HeadCell>
                      <Table.HeadCell align="left">Date</Table.HeadCell>
                      <Table.HeadCell align="left">Source</Table.HeadCell>
                      <Table.HeadCell align="left">Destination</Table.HeadCell>
                      <Table.HeadCell align="center">Type</Table.HeadCell>
                      <Table.HeadCell align="right">Amount (₹)</Table.HeadCell>
                      <Table.HeadCell align="left">Particulars</Table.HeadCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {filteredLedgerRecords.length === 0 ? (
                      <Table.EmptyRow colSpan={7} message={`No ledger movements for ${dayLabel}`} />
                    ) : (
                      <>
                        {filteredLedgerRecords.map((line, index) => (
                          <Table.Row key={line.id || index}>
                            <Table.Cell align="left">{line.id}</Table.Cell>
                            <Table.Cell align="left">{formatDate(line.date)}</Table.Cell>
                            <Table.Cell align="left">{accountNameOf(line.sourceId)}</Table.Cell>
                            <Table.Cell align="left">{accountNameOf(line.destId)}</Table.Cell>
                            <Table.Cell align="center">
                              <Badge variant={LEDGER_TYPE_TONE[line.type] || 'neutral'}>
                                {line.type}
                                {line.count > 1 ? ` ×${line.count}` : ''}
                              </Badge>
                            </Table.Cell>
                            {/* amount is PAISE — formatINR converts. */}
                            <Table.Cell align="right" className="tabular-nums">
                              {formatINR(line.amount)}
                            </Table.Cell>
                            <Table.Cell align="left">{line.particulars}</Table.Cell>
                          </Table.Row>
                        ))}
                        <Table.Row className="bg-canvas-base font-semibold">
                          <Table.Cell align="left" colSpan={5}>Money in &mdash; cash &amp; bank</Table.Cell>
                          <Table.Cell align="right" className="tabular-nums text-success-700">
                            +{formatINR(ledgerTotals.moneyIn)}
                          </Table.Cell>
                          <Table.Cell />
                        </Table.Row>
                        <Table.Row className="bg-canvas-base font-semibold">
                          <Table.Cell align="left" colSpan={5}>Money out &mdash; cash &amp; bank</Table.Cell>
                          <Table.Cell align="right" className="tabular-nums text-danger-700">
                            &minus;{formatINR(ledgerTotals.moneyOut)}
                          </Table.Cell>
                          <Table.Cell />
                        </Table.Row>
                        <Table.Row className="bg-canvas-base font-bold border-t-2 border-border-light">
                          <Table.Cell align="left" colSpan={5}>
                            Net change &mdash; {filteredLedgerRecords.length} movement
                            {filteredLedgerRecords.length === 1 ? '' : 's'}
                            {/* This is the figure the denomination count has to
                                reconcile against — it differs from the
                                Transactions tab's net, which counts members'
                                account movements rather than cash. */}
                            <span className="ml-2 font-normal text-ink-300 text-[12px]">
                              in cash &amp; bank
                            </span>
                            {/* Internal moves — a disbursement credited to the member's
                                own account, or a fee taken out of it — shift value
                                between the FPO's own accounts without any money
                                changing hands. Named so the difference between this
                                figure and the rows above is never a mystery. */}
                            {ledgerTotals.internal > 0 && (
                              <span className="ml-2 font-normal text-ink-400 text-[12px]">
                                ({formatINR(ledgerTotals.internal)} moved internally, no cash)
                              </span>
                            )}
                          </Table.Cell>
                          <Table.Cell align="right" className="tabular-nums">
                            {ledgerTotals.net >= 0 ? '+' : '−'}
                            {formatINR(Math.abs(ledgerTotals.net))}
                          </Table.Cell>
                          <Table.Cell />
                        </Table.Row>
                      </>
                    )}
                  </Table.Body>
                </Table>
              </>
            ))}
          </div>
        </div>
      </div>

      {/* Funding — comes FIRST, and suppresses the staff popup below while it
          stands. Approving a collection finalises part of the day, so it should
          not be possible over money whose source was never recorded.

          Reading a gap here is not guesswork: the operator chose the funding
          agency at disbursement, which is what sent the loan to that agency's
          own Loans Outstanding account. Comparing it against that agency's
          Borrowings account (plus Share Capital, for OWN FUND) is the whole
          test. */}
      <Modal
        isOpen={isFundingModalVisible && fundingBlocked}
        onClose={() => setIsFundingModalVisible(false)}
        // 2xl (max-w-6xl) because the table has seven columns and Table cells
        // default to whitespace-nowrap: at the default md (~448px) it scrolled
        // sideways and clipped "Short by" — the one figure the operator
        // actually needs.
        // `danger` gives the red header the component already implements; this
        // popup stops the day closing, so it should not read as a notice.
        size="2xl"
        danger
        title="Funding Not Recorded"
        subtitle="Money has been lent with no recorded source. Record it before closing the day."
        // The popup names the remedy, so it should also be the way there. The
        // sidebar unlocks these paths precisely while the matching shortfall
        // stands (Sidebar.jsx), so these buttons are not a way around the lock —
        // they are the same exemption, one click instead of hunting the menu.
        //
        // ONE BUTTON PER ACTIVE CONDITION, not either/or. unreceiptedAgencies and
        // memberBorrowings are computed independently in fundsAvailable.js and can
        // both stand at once (a disbursement with no funding receipt, on a branch
        // that has also borrowed members' savings). A single button would hide the
        // other remedy and strand the operator — the very bug this fixes. It used
        // to send everyone to /receipts, so the member-savings case pointed at the
        // wrong screen while its own text named Payments.
        //
        // Receipts first: funding must be recorded before anything else settles.
        // savingsShortfall gets no button — no screen fixes it, the cash has to
        // come back — so if that is the only condition the footer is empty.
        footer={
          <div className="flex gap-2 justify-end">
            {/* Receipts is offered for the agency case AND for member savings:
                that money also comes back through an agency drawdown or share
                capital, which are receipts. The sidebar unlocks both screens on
                the same condition, so the popup must offer both routes. */}
            {(unreceiptedAgencies.length > 0 || memberBorrowings > 0) && (
              <Button
                variant={unreceiptedAgencies.length > 0 ? 'primary' : 'secondary'}
                onClick={() => navigate('/receipts')}
              >
                Go to Receipts
              </Button>
            )}
            {memberBorrowings > 0 && (
              <Button variant="primary" onClick={() => navigate('/payments')}>
                Go to Payments
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-4 text-sm text-ink-700">
          {unreceiptedAgencies.length > 0 && (
            <div>
              <div className="font-semibold text-ink-900 mb-2">
                Disbursed more than was recorded as received
              </div>
              {/* Disbursed and Repaid are shown so the operator can see WHY
                  "Short by" differs from the Loans Outstanding ledger. Without
                  them the popup names one figure while the ledger shows another
                  and neither explains the other — which is how the amount to
                  receipt was previously read off the wrong column.

                  "Record under" carries the two selections the Receipts page
                  asks for: a receipt type (the ledger group) and an account
                  within it. Both come from the API; neither is derived here. */}
              <Table className="flex-none max-h-none overflow-visible">
                <Table.Head>
                  <tr>
                    <Table.HeadCell align="left">Source</Table.HeadCell>
                    <Table.HeadCell align="right">Disbursed (₹)</Table.HeadCell>
                    <Table.HeadCell align="right">Repaid (₹)</Table.HeadCell>
                    <Table.HeadCell align="right">Outstanding (₹)</Table.HeadCell>
                    <Table.HeadCell align="right">Recorded (₹)</Table.HeadCell>
                    <Table.HeadCell align="right">Short by (₹)</Table.HeadCell>
                    <Table.HeadCell align="left">Record under</Table.HeadCell>
                  </tr>
                </Table.Head>
                <Table.Body>
                  {unreceiptedAgencies.map((a) => (
                    <Table.Row key={a.agency}>
                      <Table.Cell align="left" className="whitespace-normal">
                        <span className="font-medium text-ink-900">{a.agency}</span>
                      </Table.Cell>
                      {/* Older API responses carry no `disbursed`; fall back to
                          the net so the row still renders rather than showing
                          a blank where the key figure belongs. */}
                      <Table.Cell align="right" className="tabular-nums">
                        {inr(a.disbursed != null ? a.disbursed : a.lent)}
                      </Table.Cell>
                      <Table.Cell align="right" className="tabular-nums">{inr(a.repaid || 0)}</Table.Cell>
                      <Table.Cell align="right" className="tabular-nums">{inr(a.lent)}</Table.Cell>
                      <Table.Cell align="right" className="tabular-nums">{inr(a.borrowed)}</Table.Cell>
                      <Table.Cell align="right" className="tabular-nums font-semibold text-danger-600">
                        {inr(a.gap)}
                      </Table.Cell>
                      {/* whitespace-normal, not the Table.Cell default of
                          nowrap: an account name is long enough that a seventh
                          nowrap column pushes the whole table into horizontal
                          scroll. */}
                      <Table.Cell align="left" className="whitespace-normal">
                        {a.receiptAccount ? (
                          <>
                            <div className="font-medium text-ink-900">{a.receiptType}</div>
                            <div className="text-[12px] text-ink-500">{a.receiptAccount}</div>
                          </>
                        ) : (
                          /* No account named — the branch is missing it, or the
                             backend predates the field. A stale server has
                             already made an old figure look current twice in
                             this work, so show a visible gap, not a guess. */
                          <span className="text-ink-500">—</span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              {/* Capped: at the 2xl width a full-bleed line of prose is hard to
                  track back to the start of the next one. */}
              {/*<p className="mt-3 max-w-2xl">
                Record it under <strong>Ledger → Receipts</strong> — receipt type{' '}
                <strong>"Borrowings"</strong> for an agency drawdown, or{' '}
                <strong>"Share Capital"</strong> for owners' money.
              </p>
              <p className="mt-2 max-w-2xl">
                Receipt the <strong>Short by</strong> amount — the money the source
                actually put in. Do not use <strong>Outstanding</strong>: that has
                already been reduced by the principal borrowers have repaid, and
                recording it would understate the funding by the amount repaid.
              </p>*/}

              {/* OWN FUND is the one ambiguous source: owners' capital is equity
                  and is never repaid, a loan from an owner is a liability that
                  is. The software cannot tell them apart, and booking a
                  repayable loan as equity needs a correcting entry to undo. */}
              {/* {unreceiptedAgencies.some((a) => a.receiptAlternative) && (
                <p className="mt-2 max-w-2xl text-[12.5px] text-ink-500">
                  {unreceiptedAgencies
                    .filter((a) => a.receiptAlternative)
                    .map((a) => (
                      <span key={a.agency}>
                        If {a.agency} was a repayable loan from an owner rather than
                        capital, record it under{' '}
                        <strong>"{a.receiptAlternative.receiptType}"</strong> →{' '}
                        <strong>{a.receiptAlternative.receiptAccount}</strong> instead.
                      </span>
                    ))}
                </p> 
              )}*/}
            </div>
          )}

          {memberBorrowings > 0 && (
            <div>
              <div className="font-semibold text-ink-900">
                ₹{inr(memberBorrowings)} of members' savings is out on loan
              </div>
              <p className="mt-1 max-w-2xl">
                Record a <strong>"Repay Member Savings"</strong> payment under{' '}
                <strong>Payments</strong> before closing the day.
              </p>
            </div>
          )}

        </div>
      </Modal>

      {/* Staff approval — blocks the close until every collection is approved.
          Held back until funding is clean, so only one prompt shows at a time. */}
      <Modal
        isOpen={isStaffCollectionsModalVisible && !fundingBlocked && !allApproved && staffCollections.length > 0}
        onClose={() => setIsStaffCollectionsModalVisible(false)}
        // lg (max-w-2xl) fits its five columns without stranding a short list in
        // a very wide panel. Not `danger` — approving collections is routine.
        size="lg"
        title="Staff Collections Approval Required"
        subtitle="Approve every collection before closing the day."
      >
        <Table className="flex-none max-h-none overflow-visible">
          <Table.Head>
            <tr>
              <Table.HeadCell align="left">Staff</Table.HeadCell>
              <Table.HeadCell align="right">Cash (₹)</Table.HeadCell>
              <Table.HeadCell align="right">Online (₹)</Table.HeadCell>
              <Table.HeadCell align="right">Total (₹)</Table.HeadCell>
              <Table.HeadCell align="center">Action</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {staffCollections.map((staff) => (
              <Table.Row key={staff.id}>
                <Table.Cell align="left">
                  <div className="font-medium text-ink-900">{staff.name}</div>
                  <div className="text-[11px] text-ink-400">ID {staff.id} · {staff.date || 'N/A'}</div>
                  {/* The deposits that make up the total. Stating the figure
                      alone left it uncheckable: the table behind this modal runs
                      past the fold, so a total of three rows against two visible
                      ones read as lost money. */}
                  {staff.breakdown?.length > 0 && (
                    <div className="text-[11px] text-ink-500 tabular-nums mt-0.5">
                      {staff.breakdown.length} deposit{staff.breakdown.length === 1 ? '' : 's'}:{' '}
                      {staff.breakdown.map((b) => inr(b.amount)).join(' + ')}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell align="right" className="tabular-nums">{inr(staff.cashCollection)}</Table.Cell>
                <Table.Cell align="right" className="tabular-nums">{inr(staff.onlineCollection)}</Table.Cell>
                <Table.Cell align="right" className="tabular-nums">{inr(staff.totalCollection)}</Table.Cell>
                <Table.Cell align="center">
                  {staff.approvedStatus ? (
                    <Badge variant="success">Approved</Badge>
                  ) : (
                    <Button size="sm" variant="primary" disabled={fundingBlocked} onClick={() => handleApprove(staff)}>Approve</Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Modal>

      <ConfirmModal
        isOpen={openModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSubmit}
        title="Confirm Submission"
        message={`Submit day-end process for ${dayLabel} (₹${inr(outstandingCash)})?`}
        confirmLabel={isSubmitting ? 'Submitting…' : 'Confirm'}
      />

      {submissionSuccess && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl
                        bg-success-600 text-white text-sm font-medium shadow-lg">
          Day-end process submitted successfully!
        </div>
      )}
    </PageLayout>
  );
};

export default DayEndProcessV2;
